import { toJsDate } from "../utils/toJsDate";
import {
  applyOptimisticApproveRejoin,
  applyOptimisticCancelInvite,
  applyOptimisticDeclineRejoin,
  applyOptimisticInviteResponse,
  applyOptimisticInviteUsers,
  applyOptimisticLeave,
  applyOptimisticParticipantKick,
} from "../utils/activityOptimisticState";
import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  writeBatch,
  Timestamp,
  addDoc,
  runTransaction,
  startAfter,
  type FieldValue,
  documentId,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { db, analytics, crashlytics } from "../services/firebase";
import {
  type ActivityRequest,
  type Chat,
  type ChatLocationPayload,
  type ChatMediaAttachment,
  type ChatMessage,
  type Notification,
  type User,
  type VibeCheckUser,
  type AuditLog,
  type AuditLogType,
  type ActivityCategory,
  UserActionType,
} from "../types";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import { queueUserAction as queueUserActionService } from "../services/userActionService";
import { getActivityImageUrl } from "../services/imageService";
import {
  sendChatMessage,
  updateChatMessage,
  updateMessageReaction,
  toggleStarMessage as toggleStarMessageCF,
  forwardMessages as forwardMessagesCF,
  syncChatActivityStatus,
  markNotificationsAsRead,
  createActivity as createActivityCF,
  joinActivity as joinActivityCF,
  deleteActivity as deleteActivityCF,
  updateActivity as updateActivityCF,
  leaveActivity as leaveActivityCF,
  kickParticipant as kickParticipantCF,
  passActivity as passActivityCF,
  approveRejoinRequest as approveRejoinRequestCF,
  declineRejoinRequest as declineRejoinRequestCF,
  undeleteActivity as undeleteActivityCF,
  inviteUsersToActivity as inviteUsersToActivityCF,
  cancelInvite as cancelInviteCF,
  respondToInvite as respondToInviteCF,
  getHomeFeedPage,
  getActivityById,
  getForYouRecommendations as getForYouRecommendationsCF,
  generateActivitySearchIdeas as generateActivitySearchIdeasCF,
} from "../services/functionsApi";

interface ActivityContextType {
  activities: ActivityRequest[];
  chats: Chat[];
  notifications: Notification[];
  auditLogs: AuditLog[];
  recommendations: (ActivityRequest & { reason: string })[];
  vibeCheckUsers: VibeCheckUser[];
  isLoading: boolean;
  isLoadingRecommendations: boolean;
  isLoadingMore: boolean;
  isLoadingAuditLogs: boolean;
  hasMoreActivities: boolean;
  refreshing: boolean;
  passedActivityIds: string[];
  loadAuditLogs: () => Promise<void>;
  createActivity: (
    newActivityData: Omit<
      ActivityRequest,
      | "id"
      | "creator"
      | "currentSize"
      | "isActive"
      | "createdAt"
      | "participants"
      | "chatId"
      | "reason"
      | "isDeleted"
      | "deletedAt"
    >,
  ) => Promise<string | undefined>;
  updateActivity: (updatedActivityData: ActivityRequest) => Promise<void>;
  deleteActivity: (activityId: string) => Promise<void>;
  undeleteActivity: (activityId: string) => Promise<void>;
  joinActivity: (
    activityId: string,
  ) => Promise<{ success: boolean; isFull: boolean; isRequestSent?: boolean }>;
  leaveActivity: (activityId: string) => Promise<void>;
  kickParticipant: (activityId: string, participantId: string) => Promise<void>;
  approveRejoinRequest: (activityId: string, userId: string) => Promise<void>;
  declineRejoinRequest: (activityId: string, userId: string) => Promise<void>;
  passActivity: (activityId: string) => Promise<void>;
  inviteUsersToActivity: (
    activityId: string,
    userIds: string[],
  ) => Promise<void>;
  respondToInvite: (activityId: string, accept: boolean) => Promise<void>;
  cancelInvite: (activityId: string, userId: string) => Promise<void>;
  getUnreadMessageCount: (userId: string) => number;
  getUnreadGroupChatCount: (userId: string) => number;
  getUnreadDirectChatCount: (userId: string) => number;
  getChatsForUser: (userId: string) => Chat[];
  getChatById: (chatId: string) => Chat | undefined;
  getDirectChatId: (otherUserId: string) => string | undefined;
  createOrGetDirectChat: (otherUserId: string) => Promise<string>;
  sendMessage: (
    chatId: string,
    text: string,
    replyTo?: {
      messageId: string;
      senderId: string;
      senderUsername: string;
      text: string;
    },
    options?: {
      format?: "plain" | "whatsapp";
      media?: ChatMediaAttachment[];
      location?: ChatLocationPayload;
      isForwarded?: boolean;
      clientRequestId?: string;
    },
  ) => Promise<void>;
  toggleStarMessage: (chatId: string, messageId: string) => Promise<boolean>;
  forwardMessages: (
    sourceChatId: string,
    messageIds: string[],
    targetChatIds: string[],
  ) => Promise<number>;
  addReactionToMessage: (
    chatId: string,
    messageId: string,
    emoji: string,
  ) => Promise<void>;
  editMessage: (
    chatId: string,
    messageId: string,
    text: string,
    format?: "plain" | "whatsapp",
  ) => Promise<void>;
  deleteMessage: (
    chatId: string,
    messageId: string,
    mode: "for_me" | "for_everyone",
  ) => Promise<void>;
  markMessageAsDelivered: (chatId: string, messageId: string) => Promise<void>;
  markMessageAsRead: (chatId: string, messageId: string) => Promise<void>;
  markChatNotificationsAsRead: (chatId: string) => void;
  markJoinNotificationsAsRead: () => void;
  markNotificationsAsReadByType: (
    type: Notification["type"],
    fromUserId: string,
  ) => void;
  markNotificationAsRead: (notificationId: string) => void;
  markAllNotificationsAsRead: () => Promise<void>;
  getUnreadNotificationCount: (userId: string) => number;
  getAuditLogsForUser: (userId: string) => AuditLog[];
  getAuditLogsForActivity: (activityId: string) => AuditLog[];
  createUserJoinedAuditLog: () => Promise<void>;
  fetchRecommendations: () => Promise<boolean>;
  prefetchIdeas: () => Promise<void>;
  prefetchedIdeas: any | null;
  getUnreadMessageCountForChat: (chatId: string) => number;
  loadMoreActivities: (options?: { category?: string }) => Promise<void>;
  refreshActivities: (options?: { category?: string }) => Promise<void>;
  refreshSingleActivity: (
    activityId: string,
  ) => Promise<ActivityRequest | null>;
  // Note: User profile updates are handled server-side by Cloud Function (runs every 2 hours)
  queueUserAction: (
    type: UserActionType,
    activityId?: string,
    activityTitle?: string,
    category?: string,
    location?: string,
    metadata?: Record<string, any>,
  ) => Promise<void>;
}

type ActivityActionsContextType = Pick<
  ActivityContextType,
  | "loadAuditLogs"
  | "createActivity"
  | "updateActivity"
  | "deleteActivity"
  | "undeleteActivity"
  | "joinActivity"
  | "leaveActivity"
  | "kickParticipant"
  | "approveRejoinRequest"
  | "declineRejoinRequest"
  | "passActivity"
  | "inviteUsersToActivity"
  | "respondToInvite"
  | "cancelInvite"
  | "createOrGetDirectChat"
  | "sendMessage"
  | "toggleStarMessage"
  | "forwardMessages"
  | "addReactionToMessage"
  | "editMessage"
  | "deleteMessage"
  | "markMessageAsDelivered"
  | "markMessageAsRead"
  | "markChatNotificationsAsRead"
  | "markJoinNotificationsAsRead"
  | "markNotificationsAsReadByType"
  | "markNotificationAsRead"
  | "markAllNotificationsAsRead"
  | "createUserJoinedAuditLog"
  | "fetchRecommendations"
  | "prefetchIdeas"
  | "loadMoreActivities"
  | "refreshActivities"
  | "refreshSingleActivity"
  | "queueUserAction"
>;

type ActivityFeedDataContextType = Pick<
  ActivityContextType,
  | "activities"
  | "isLoading"
  | "isLoadingMore"
  | "hasMoreActivities"
  | "refreshing"
  | "passedActivityIds"
>;

type ActivityChatDataContextType = Pick<
  ActivityContextType,
  | "chats"
  | "getUnreadMessageCount"
  | "getUnreadGroupChatCount"
  | "getUnreadDirectChatCount"
  | "getChatsForUser"
  | "getChatById"
  | "getDirectChatId"
  | "getUnreadMessageCountForChat"
>;

type ActivityNotificationsDataContextType = Pick<
  ActivityContextType,
  | "notifications"
  | "getUnreadNotificationCount"
>;

type ActivityRecommendationsDataContextType = Pick<
  ActivityContextType,
  | "recommendations"
  | "vibeCheckUsers"
  | "isLoadingRecommendations"
  | "prefetchedIdeas"
>;

type ActivityAuditDataContextType = Pick<
  ActivityContextType,
  | "auditLogs"
  | "isLoadingAuditLogs"
  | "getAuditLogsForUser"
  | "getAuditLogsForActivity"
>;

const areStringArraysEqual = (left: string[] | undefined, right: string[] | undefined) => {
  const normalizedLeft = left || [];
  const normalizedRight = right || [];

  if (normalizedLeft.length !== normalizedRight.length) return false;

  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

const areTypingMapsEqual = (
  left: Record<string, string> | undefined,
  right: Record<string, string> | undefined,
) => {
  const leftEntries = Object.entries(left || {}).sort(([a], [b]) => a.localeCompare(b));
  const rightEntries = Object.entries(right || {}).sort(([a], [b]) => a.localeCompare(b));

  if (leftEntries.length !== rightEntries.length) return false;

  return leftEntries.every(([key, value], index) => {
    const [otherKey, otherValue] = rightEntries[index] || [];
    return key === otherKey && value === otherValue;
  });
};

const areMediaAttachmentsEqual = (
  left: ChatMediaAttachment[] | undefined,
  right: ChatMediaAttachment[] | undefined,
) => {
  const normalizedLeft = left || [];
  const normalizedRight = right || [];

  if (normalizedLeft.length !== normalizedRight.length) return false;

  return normalizedLeft.every((media, index) => {
    const other = normalizedRight[index];
    return (
      media.id === other?.id &&
      media.type === other?.type &&
      media.url === other?.url &&
      media.mimeType === other?.mimeType &&
      media.fileName === other?.fileName &&
      media.size === other?.size
    );
  });
};

const areReactionsEqual = (
  left: ChatMessage["reactions"],
  right: ChatMessage["reactions"],
) => {
  const normalizedLeft = left || [];
  const normalizedRight = right || [];

  if (normalizedLeft.length !== normalizedRight.length) return false;

  return normalizedLeft.every((reaction, index) => {
    const other = normalizedRight[index];
    return (
      reaction.emoji === other?.emoji &&
      reaction.userId === other?.userId &&
      reaction.timestamp === other?.timestamp
    );
  });
};

const areRepliesEqual = (
  left: ChatMessage["replyTo"],
  right: ChatMessage["replyTo"],
) => {
  if (left === right) return true;
  if (!left || !right) return false;

  return (
    left.messageId === right.messageId &&
    left.senderId === right.senderId &&
    left.senderUsername === right.senderUsername &&
    left.text === right.text
  );
};

const areLocationsEqual = (
  left: ChatLocationPayload | undefined,
  right: ChatLocationPayload | undefined,
) => {
  if (left === right) return true;
  if (!left && !right) return true;
  if (!left || !right) return false;

  return (
    left.kind === right.kind &&
    left.latitude === right.latitude &&
    left.longitude === right.longitude &&
    left.label === right.label &&
    left.placeName === right.placeName &&
    left.accuracyMeters === right.accuracyMeters &&
    left.sharedAt === right.sharedAt &&
    left.liveShareId === right.liveShareId &&
    left.expiresAt === right.expiresAt
  );
};

const areMessagesEqual = (left: ChatMessage[], right: ChatMessage[]) => {
  if (left.length !== right.length) return false;

  return left.every((message, index) => {
    const other = right[index];
    return (
      message.id === other?.id &&
      message.clientRequestId === other?.clientRequestId &&
      message.senderId === other?.senderId &&
      message.text === other?.text &&
      message.format === other?.format &&
      message.timestamp === other?.timestamp &&
      message.editedAt === other?.editedAt &&
      message.deletedForEveryone === other?.deletedForEveryone &&
      message.status === other?.status &&
      areLocationsEqual(message.location, other?.location) &&
      message.readAt === other?.readAt &&
      message.deliveredAt === other?.deliveredAt &&
      message.isForwarded === other?.isForwarded &&
      areMediaAttachmentsEqual(message.media, other?.media) &&
      areStringArraysEqual(message.deletedForUsers, other?.deletedForUsers) &&
      areRepliesEqual(message.replyTo, other?.replyTo) &&
      areStringArraysEqual(message.readBy, other?.readBy) &&
      areStringArraysEqual(message.deliveredTo, other?.deliveredTo) &&
      areReactionsEqual(message.reactions, other?.reactions) &&
      areStringArraysEqual(message.starredBy, other?.starredBy)
    );
  });
};

const areChatsEquivalent = (left: Chat, right: Chat) => {
  return (
    left.id === right.id &&
    left.activityId === right.activityId &&
    left.activityTitle === right.activityTitle &&
    left.activityImageUrl === right.activityImageUrl &&
    left.activityStatus === right.activityStatus &&
    areStringArraysEqual(left.participants, right.participants) &&
    areStringArraysEqual(left.leftParticipantIds, right.leftParticipantIds) &&
    areTypingMapsEqual(left.typing, right.typing) &&
    areMessagesEqual(left.messages, right.messages)
  );
};

const stabilizeChats = (previousChats: Chat[], nextChats: Chat[]) => {
  const previousById = new Map(previousChats.map((chat) => [chat.id, chat]));
  const stabilized = nextChats.map((chat) => {
    const previous = previousById.get(chat.id);
    if (!previous) return chat;
    return areChatsEquivalent(previous, chat) ? previous : chat;
  });

  if (
    previousChats.length === stabilized.length &&
    previousChats.every((chat, index) => chat === stabilized[index])
  ) {
    return previousChats;
  }

  return stabilized;
};

const ActivityFeedDataContext = createContext<
  ActivityFeedDataContextType | undefined
>(undefined);
const ActivityChatDataContext = createContext<
  ActivityChatDataContextType | undefined
>(undefined);
const ActivityNotificationsDataContext = createContext<
  ActivityNotificationsDataContextType | undefined
>(undefined);
const ActivityRecommendationsDataContext = createContext<
  ActivityRecommendationsDataContextType | undefined
>(undefined);
const ActivityAuditDataContext = createContext<
  ActivityAuditDataContextType | undefined
>(undefined);
const ActivityActionsContext = createContext<
  ActivityActionsContextType | undefined
>(
  undefined,
);

const ACTIVITIES_PER_PAGE = 20; // Page size for pagination
const INITIAL_LOAD_MULTIPLIER = 2; // Load 2x page size initially (40 activities) instead of 5x

const isCallableNetworkLikeError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const maybeError = error as { code?: unknown; message?: unknown; name?: unknown };
  const code =
    typeof maybeError.code === "string"
      ? maybeError.code.toLowerCase()
      : "";
  const message =
    typeof maybeError.message === "string"
      ? maybeError.message.toLowerCase()
      : "";

  return (
    code === "functions/internal" ||
    code === "functions/unavailable" ||
    message.includes("cors") ||
    message.includes("network") ||
    message.includes("failed to fetch")
  );
};

// Exportable hydrateActivities function
export async function hydrateActivities(
  activityDocs: any[],
  getUsersByIds: (ids: string[]) => Promise<User[]>,
): Promise<ActivityRequest[]> {
  const userIds = new Set<string>();
  activityDocs.forEach((doc) => {
    if (
      doc.creatorId &&
      typeof doc.creatorId === "string" &&
      doc.creatorId.trim() !== ""
    ) {
      userIds.add(doc.creatorId);
    }
    if (Array.isArray(doc.participantIds)) {
      doc.participantIds.forEach((id: string) => {
        if (typeof id === "string" && id.trim() !== "") {
          userIds.add(id);
        }
      });
    }
  });
  const users = await getUsersByIds(Array.from(userIds));
  // Only include users that are defined and have a valid id
  const validUsers = (users || []).filter((u) => u && u.id);
  const userMap = new Map(validUsers.map((u) => [u?.id, u]));
  return activityDocs
    .filter((doc) => !!doc && !!doc.id)
    .map((doc) => {
      try {
        if (!doc || !doc.id) {
          return null;
        }
        if (!doc.creatorId) {
          return null;
        }
        if (!Array.isArray(doc.participantIds)) {
          return null;
        }
        const creator = userMap.get(doc.creatorId);
        const participants = doc.participantIds
          .map((id: string) => userMap.get(id))
          .filter((u: User | undefined): u is User => !!u);
        if (!creator) {
          return null;
        }
        return {
          ...doc,
          creator: creator,
          participants: participants,
          currentSize: participants.length,
          id: doc.id,
          createdAt: toJsDate(doc.createdAt).toISOString(),
          dateTime: toJsDate(doc.dateTime).toISOString(),
          deletedAt:
            doc.deletedAt instanceof Timestamp
              ? doc.deletedAt.toDate().toISOString()
              : null,
        } as ActivityRequest;
      } catch (err) {
        crashlytics.logError(
          `hydrateActivities: Error hydrating doc ${doc?.id || "unknown"}`,
          "ActivityContext.tsx",
          err,
        );
        return null;
      }
    })
    .filter((a): a is ActivityRequest => a !== null);
}

export const ActivityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser, getUsersByIds } = useAuth();
  const { addToast } = useToast();

  const [activities, setActivities] = useState<ActivityRequest[]>([]);
  // Map of all activities ever fetched, including inactive/deleted, for chat status correction
  const [allActivitiesById, setAllActivitiesById] = useState<Map<string, ActivityRequest>>(new Map());
  const [rawChats, setRawChats] = useState<Chat[]>([]);
  const [chats, setChats] = useState<Chat[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAuditLogs, setIsLoadingAuditLogs] = useState(false);

  const [recommendations, setRecommendations] = useState<
    (ActivityRequest & { reason: string })[]
  >([]);
  const [vibeCheckUsers, setVibeCheckUsers] = useState<VibeCheckUser[]>([]);
  const [prefetchedIdeas, setPrefetchedIdeas] = useState<any | null>(null);
  const [isLoadingRecommendations, setIsLoadingRecommendations] =
    useState(false);
  const [lastRecommendationFetch, setLastRecommendationFetch] =
    useState<number>(0);
  const [recommendationsCacheKey, setRecommendationsCacheKey] =
    useState<string>("");

  // Pagination state
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreActivities, setHasMoreActivities] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastActivityDoc, setLastActivityDoc] = useState<string | null>(null);
  const [allActivityIds, setAllActivityIds] = useState<Set<string>>(new Set());

  const passedActivityIds = useMemo(
    () => currentUser?.passedActivityIds || [],
    [currentUser],
  );

  // Helper function to hydrate activities for context usage
  const contextHydrateActivities = useCallback(
    async (activityDocs: any[]): Promise<ActivityRequest[]> => {
      return hydrateActivities(activityDocs, getUsersByIds);
    },
    [getUsersByIds],
  );

  // Single place to decide if an activity should be shown in the feed
  const shouldIncludeActivity = useCallback(
    (activity: ActivityRequest) => {
      if (!currentUser) return false;
      const requesterId = currentUser.id;

      if (activity.isDeleted || activity.isActive === false) return false;
      if ((activity as any).isReported || (activity as any).isPrivate) return false;
      if (!activity.creator?.id || activity.creator.id === requesterId) return false;
      if (activity.participants.some((p) => p.id === requesterId)) return false;
      if (passedActivityIds.includes(activity.id)) return false;

      const currentSize = activity.currentSize || activity.participants.length || 0;
      if (activity.groupSize && currentSize >= activity.groupSize) return false;

      const endTime =
        toJsDate(activity.dateTime).getTime() + (activity.duration || 0) * 60 * 1000;
      if (Date.now() > endTime) return false;

      return true;
    },
    [currentUser, passedActivityIds],
  );

  // Load more activities (Cloud Function)
  const loadMoreActivities = useCallback(async (options?: { category?: string }) => {
    if (!currentUser || !hasMoreActivities || isLoadingMore) {
      return;
    }
    setIsLoadingMore(true);
    try {
      const pageSize = ACTIVITIES_PER_PAGE;
      const cursor = lastActivityDoc ? lastActivityDoc : null;
      const response = await getHomeFeedPage({
        cursor,
        pageSize,
        category: options?.category,
      });
      const hydratedActivities = (await hydrateActivities(
        response.activities,
        getUsersByIds,
      )).filter(shouldIncludeActivity);

      // Use functional update to avoid race conditions
      if (hydratedActivities.length > 0) {
        setActivities((prev) => {
          // Create set of existing IDs for fast lookup
          const existingIds = new Set(prev.map(a => a.id));
          // Filter out duplicates
          const newActivities = hydratedActivities.filter(
            (a) => !existingIds.has(a.id),
          );
          // Only append if we have new activities
          return newActivities.length > 0 ? [...prev, ...newActivities] : prev;
        });

        setAllActivityIds((prev) => {
          const newSet = new Set(prev);
          hydratedActivities.forEach((a) => newSet.add(a.id));
          return newSet;
        });
      }
      setLastActivityDoc(response.nextCursor);
      setHasMoreActivities(response.hasMore);
    } catch (error) {
      crashlytics.logError(
        `Error loading more activities (Cloud Function): ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    currentUser,
    hasMoreActivities,
    isLoadingMore,
    lastActivityDoc,
    hydrateActivities,
    allActivityIds,
    getUsersByIds,
    shouldIncludeActivity,
  ]);

  // Refresh activities (Cloud Function)
  const refreshActivities = useCallback(async (options?: { category?: string }) => {
    if (!currentUser || refreshing) {
      return;
    }
    setRefreshing(true);
    // Safety timeout to prevent stuck refreshing state
    const timeoutId = setTimeout(() => {
      console.warn(
        "⚠️ ActivityContext: Refresh timeout, forcing setRefreshing(false)",
      );
      setRefreshing(false);
    }, 10000); // 10 second timeout
    // Store current activities as fallback
    const currentActivities = activities;
    const currentLastDoc = lastActivityDoc;
    const currentAllIds = allActivityIds;
    const currentHasMore = hasMoreActivities;
    try {
      // Fetch the first page of fresh activities (don't reset pagination)
      const response = await getHomeFeedPage({
        cursor: null,
        pageSize: ACTIVITIES_PER_PAGE,
        category: options?.category,
      });
      const hydratedActivities = (await hydrateActivities(
        response.activities,
        getUsersByIds,
      )).filter(shouldIncludeActivity);
      if (hydratedActivities.length === 0 && response.activities.length > 0) {
        crashlytics.logError(
          "Hydration failed - all activities filtered out",
          "ActivityContext.tsx",
          new Error("Hydration failed - all activities filtered out"),
        );
        setRefreshing(false);
        return;
      }

      // Merge new activities with existing ones using functional update to avoid stale closure issues
      setActivities((prevActivities) => {
        const validPrevActivities = prevActivities.filter(shouldIncludeActivity);
        const existingIds = new Set(prevActivities.map(a => a.id));
        const newActivities = hydratedActivities.filter(a => !existingIds.has(a.id));
        // Combine: new activities first, then existing ones
        return [...newActivities, ...validPrevActivities];
      });

      // Update allActivityIds set using functional update
      setAllActivityIds((prevIds) => {
        const newIds = new Set(prevIds);
        hydratedActivities.forEach(a => newIds.add(a.id));
        return newIds;
      });
      // Don't reset lastActivityDoc or hasMoreActivities - we want to keep being able to page through old activities
    } catch (error) {
      crashlytics.logError(
        `Error refreshing activities (Cloud Function): ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
      // Restore previous state on error
      setActivities(currentActivities);
      setLastActivityDoc(currentLastDoc);
      setAllActivityIds(currentAllIds);
      setHasMoreActivities(currentHasMore);
    } finally {
      clearTimeout(timeoutId);
      setRefreshing(false);
    }
  }, [
    currentUser,
    hydrateActivities,
    activities,
    lastActivityDoc,
    allActivityIds,
    hasMoreActivities,
    refreshing,
    getUsersByIds,
  ]);

  const refreshSingleActivity = useCallback(
    async (activityId: string): Promise<ActivityRequest | null> => {
      if (!currentUser) return null;

      const applyFreshActivity = (freshActivity: ActivityRequest) => {
        // Always add to allActivitiesById for chat status correction
        setAllActivitiesById((prev) => {
          const next = new Map(prev);
          next.set(freshActivity.id, freshActivity);
          return next;
        });
        // Only add to activities if it should be included in feed
        setActivities((prevActivities) => {
          if (!shouldIncludeActivity(freshActivity)) {
            return prevActivities.filter((activity) => activity.id !== activityId);
          }

          const index = prevActivities.findIndex(
            (activity) => activity.id === activityId,
          );
          if (index === -1) {
            return [...prevActivities, freshActivity];
          }
          const updatedActivities = [...prevActivities];
          updatedActivities[index] = freshActivity;
          return updatedActivities;
        });
      };

      // NOTE: With single comprehensive activity subscription, this function
      // is largely redundant as ALL activities auto-update in real-time.
      // Kept for backward compatibility with existing code that calls it directly.
      // In most cases, the subscription will have already updated the activity.

      try {
        const response = await getActivityById({ activityId });
        if (!response.success || !response.activity) {
          return null;
        }

        const hydrated = await hydrateActivities(
          [response.activity],
          getUsersByIds,
        );

        if (hydrated.length === 0) {
          return null;
        }

        const freshActivity = hydrated[0];
        applyFreshActivity(freshActivity);

        return freshActivity;
      } catch (error) {
        if (db && isCallableNetworkLikeError(error)) {
          try {
            const fallbackSnap = await getDoc(doc(db, "activities", activityId));
            if (!fallbackSnap.exists()) {
              return null;
            }

            const fallbackHydrated = await hydrateActivities(
              [{ id: fallbackSnap.id, ...(fallbackSnap.data() as Record<string, unknown>) }],
              getUsersByIds,
            );

            if (fallbackHydrated.length > 0) {
              const freshActivity = fallbackHydrated[0];
              applyFreshActivity(freshActivity);
              crashlytics.log(
                `refreshSingleActivity fallback to Firestore succeeded for ${activityId}`,
              );
              return freshActivity;
            }
          } catch (fallbackError) {
            crashlytics.logError(
              `refreshSingleActivity Firestore fallback failed for ${activityId}`,
              "ActivityContext.tsx",
              fallbackError,
            );
          }
        }

        crashlytics.logError(
          `Error refreshing activity ${activityId} (CF): ${JSON.stringify(error)}`,
          "ActivityContext.tsx",
          error,
        );
        return null;
      }
    },
    [currentUser, hydrateActivities, getUsersByIds, shouldIncludeActivity],
  );

  // Lazy load audit logs on-demand (called when user views audit log page)
  const loadAuditLogs = useCallback(async () => {
    if (!currentUser || !db || isLoadingAuditLogs) return;

    // If already loaded, don't reload
    if (auditLogs.length > 0) return;

    setIsLoadingAuditLogs(true);

    try {
      const auditLogCollectionRef = collection(db, "auditLogs");
      const auditLogQuery = query(
        auditLogCollectionRef,
        orderBy("createdAt", "desc"),
        limit(50), // Load recent logs only
      );

      const snapshot = await getDocs(auditLogQuery);

      const logDocs = snapshot.docs.map((doc) => ({
        ...(doc.data() as any),
        id: doc.id,
      }));

      const fromUserIds = new Set<string>(
        logDocs.map((l) => l.fromUserId).filter((id): id is string => !!id),
      );
      const users = await getUsersByIds(Array.from(fromUserIds));
      const userMap = new Map(users.map((u) => [u.id, u]));

      const hydratedLogs = logDocs.map(
        (doc) =>
          ({
            ...doc,
            id: doc.id,
            fromUser: doc.fromUserId
              ? userMap.get(doc.fromUserId)
              : undefined,
            createdAt:
              doc.createdAt instanceof Timestamp
                ? doc.createdAt.toDate().toISOString()
                : new Date(doc.createdAt).toISOString(),
          }) as AuditLog,
      );

      setAuditLogs(hydratedLogs);
    } catch (error) {
      crashlytics.logError(
        `Error loading audit logs on-demand: ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
    } finally {
      setIsLoadingAuditLogs(false);
    }
  }, [currentUser, db, isLoadingAuditLogs, auditLogs.length, getUsersByIds]);

  // Queue a user action for profile update (doesn't update immediately)
  // Note: User profile updates are handled server-side by Cloud Function
  // Client only queues actions via queueUserAction()
  // Cloud Function runs every 2 hours and processes batches
  const queueUserAction = useCallback(
    async (
      type: UserActionType,
      activityId?: string,
      activityTitle?: string,
      category?: string,
      location?: string,
      metadata?: Record<string, any>,
    ) => {
      if (!currentUser) return;

      // Delegate to the shared service
      await queueUserActionService(
        currentUser.id,
        type,
        activityId,
        activityTitle,
        category,
        location,
        metadata,
      );
    },
    [currentUser],
  );

  const runActivityBackgroundTask = useCallback(
    (task: Promise<unknown>, description: string) => {
      void task.catch((error) => {
        crashlytics.logError(
          `${description}: ${JSON.stringify((error as any)?.message || error)}`,
          "ActivityContext.tsx",
          error,
        );
      });
    },
    [],
  );

  const deriveActivityStatus = useCallback(
    (activity?: ActivityRequest | null): "active" | "inactive" | "unknown" => {
      if (!activity) return "unknown";
      if (activity.isDeleted || activity.isActive === false) return "inactive";

      const startMs = toJsDate(activity.dateTime).getTime();
      const durationMs = Number(activity.duration || 0) * 60 * 1000;
      const endMs = startMs + durationMs;

      if (Number.isFinite(endMs) && endMs < Date.now()) return "inactive";
      return "active";
    },
    [],
  );

  // Data Hydration Effect
  useEffect(() => {
    if (!currentUser) {
      // Clear all data immediately when user logs out
      setActivities([]);
      setRawChats([]);
      setChats([]);
      setNotifications([]);
      setAuditLogs([]);
      setLastActivityDoc(null);
      setAllActivityIds(new Set());
      setHasMoreActivities(true);
      setIsLoading(false);
      return;
    }
    if (!db) return;

    // Note: 3 listeners total (activities, chats, notifications)
    // Audit logs are loaded on-demand via loadAuditLogs()
    const collections = ["activities", "chats", "notifications"];
    let loadedCount = 0;

    const handleLoad = () => {
      loadedCount++;
      if (loadedCount === collections.length) {
        setIsLoading(false);
      }
    };


    // Single real-time listener for ALL activities
    // Fetches a reasonable initial batch with limit to prevent loading entire database
    // loadMoreActivities() handles pagination beyond this initial set
    const activityCollectionRef = collection(db, "activities");

    // Log which database we're connecting to
    console.log("📡 ActivityContext: Setting up activity subscription", {
      dbConfig: db?.app?.options,
      useEmulator: (db as any)?._settings?.host?.includes("localhost") || (db as any)?._settings?.host?.includes("127.0.0.1"),
      emulatorHost: (db as any)?._settings?.host,
    });

    const activityQuery = query(
      activityCollectionRef,
      orderBy("createdAt", "desc"),
      limit(ACTIVITIES_PER_PAGE * INITIAL_LOAD_MULTIPLIER), // Fetch 40 activities (was 100)
    );
    const activityUnsub = onSnapshot(activityQuery, {
      next: async (snapshot) => {
        if (!currentUser) return;

        console.log(`📥 ActivityContext: Received ${snapshot.docs.length} activities from subscription (metadata.fromCache: ${snapshot.metadata.fromCache})`);

        let activityDocs = snapshot.docs.map((doc) => ({
          ...(doc.data() as any),
          id: doc.id,
        }));

        // Client-side filtering to mirror backend protections (for subscription data)
        activityDocs = activityDocs.filter((doc) => {
          const creatorId = String(doc.creatorId || "");
          const requesterId = String(currentUser.id);

          // Filter out deleted/inactive/reported/private
          if (doc.isDeleted || !doc.isActive || doc.isReported || doc.isPrivate) {
            return false;
          }

          // Filter out own activities
          if (!creatorId || creatorId === requesterId) {
            return false;
          }

          // Filter out activities where user is already a participant
          if (
            Array.isArray(doc.participantIds) &&
            doc.participantIds.map((id: any) => String(id)).includes(requesterId)
          ) {
            return false;
          }

          // Filter out passed activities
          if (passedActivityIds.map((id: any) => String(id)).includes(doc.id)) {
            return false;
          }

          // Filter out full activities
          const currentSize = Number(doc.currentSize || 0);
          const groupSize = Number(doc.groupSize || 0);
          if (groupSize > 0 && currentSize >= groupSize) {
            return false;
          }

          // Filter out expired activities
          const isExpired =
            new Date() >
            new Date(
              toJsDate(doc.dateTime).getTime() + doc.duration * 60 * 1000,
            );
          if (isExpired) return false;

          return true;
        });

        const hydratedActivities = await hydrateActivities(
          activityDocs,
          getUsersByIds,
        );

        // Merge subscription data with any paginated activities to avoid losing them
        setActivities((prevActivities) => {
          // Create a map of subscription activity IDs for fast lookup
          const subscriptionIds = new Set(hydratedActivities.map(a => a.id));

          // Keep paginated activities that are not in the subscription
          // and still valid for the feed
          const paginatedActivities = prevActivities.filter(
            a => !subscriptionIds.has(a.id) && shouldIncludeActivity(a)
          );

          // Combine: subscription activities (always fresh) + paginated activities
          const merged = [...hydratedActivities, ...paginatedActivities];

          // Verify no duplicates in final result
          const finalIds = new Set<string>();
          const duplicates: string[] = [];
          merged.forEach(a => {
            if (finalIds.has(a.id)) {
              duplicates.push(a.id);
            }
            finalIds.add(a.id);
          });

          if (duplicates.length > 0) {
            crashlytics.logError(
              `❌ ActivityContext: Duplicates detected after subscription merge: ${duplicates.join(",")}`,
              "ActivityContext.tsx",
              new Error("Duplicate activities after subscription merge"),
            );
          }

          return merged;
        });

        setAllActivityIds((prevIds) => {
          const newIds = new Set(hydratedActivities.map((a) => a.id));
          // Also include any paginated activity IDs
          prevIds.forEach(id => newIds.add(id));
          return newIds;
        });
        // Store the last document ID as a string cursor for pagination
        const lastDoc = snapshot.docs[snapshot.docs.length - 1];
        setLastActivityDoc(lastDoc ? lastDoc.id : null);
        // Set hasMore based on filtered docs so the flag reflects what is actually renderable
        setHasMoreActivities(
          activityDocs.length >= ACTIVITIES_PER_PAGE * INITIAL_LOAD_MULTIPLIER,
        );
        handleLoad();
      },
      error: (error) => {
        crashlytics.logError(
          `❌ ActivityContext: Subscription error code=${(error as any)?.code || "unknown"} message=${error?.message || "unknown"}`,
          "ActivityContext.tsx",
          error,
        );
        crashlytics.logError(
          `Error loading activities: ${JSON.stringify(error?.message)}`,
          "ActivityContext.tsx",
          error,
        );
        if (!currentUser) return;
      },
    });

    const chatCollectionRef = collection(db, "chats");
    const chatQuery = query(
      chatCollectionRef,
      where("participants", "array-contains", currentUser.id),
    );
    const chatUnsub = onSnapshot(chatQuery, {
      next: async (snapshot) => {
        // Check if user is still authenticated before processing
        if (!currentUser) return;

        const chatData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Chat[];

        setRawChats((prevChats) => stabilizeChats(prevChats, chatData));
        handleLoad();
      },
      error: (error) => {
        crashlytics.logError(
          `Error loading chats currentUserId=${currentUser?.id} Error: ${JSON.stringify(error?.message)}`,
          "ActivityContext.tsx",
          error,
        );
        // If user is not authenticated, ignore the error
        if (!currentUser) return;
      },
    });

    const notificationCollectionRef = collection(db, "notifications");
    const notificationQuery = query(
      notificationCollectionRef,
      where("userId", "==", currentUser.id),
      orderBy("createdAt", "desc"),
      limit(30), // Reduced from 50 - load recent notifications, more on-demand
    );
    const notificationUnsub = onSnapshot(notificationQuery, {
      next: async (snapshot) => {
        // Check if user is still authenticated before processing
        if (!currentUser) return;

        const notifDocs = snapshot.docs.map((doc) => ({
          ...(doc.data() as any),
          id: doc.id,
        }));

        const fromUserIds = new Set<string>(
          notifDocs.map((n) => n.fromUserId).filter((id): id is string => !!id),
        );
        const users = await getUsersByIds(Array.from(fromUserIds));
        const userMap = new Map(users.map((u) => [u.id, u]));

        const hydratedNotifications: Notification[] = notifDocs
          .map(
            (doc) =>
              ({
                ...doc,
                id: doc.id,
                fromUser: userMap.get(doc.fromUserId) || 'system',
                createdAt:
                  doc.createdAt instanceof Timestamp
                    ? doc.createdAt.toDate().toISOString()
                    : new Date(doc.createdAt).toISOString(),
              }) as Notification,
          )
          .filter((n) => n.fromUser);

        // All activities are covered by subscriptions, no need to fetch
        setNotifications(hydratedNotifications);
        handleLoad();
      },
      error: (error) => {
        crashlytics.logError(
          `Error loading notifications currentUserId=${currentUser?.id} Error: ${JSON.stringify(error?.message)}`,
          "ActivityContext.tsx",
          error,
        );
        // If user is not authenticated, ignore the error
        if (!currentUser) return;
      },
    });

    // Audit logs are now loaded on-demand via loadAuditLogs() - not subscribed at startup

    return () => {
      activityUnsub();
      chatUnsub();
      notificationUnsub();
    };
  }, [currentUser, getUsersByIds, passedActivityIds]);

  // Track pending activity fetches to avoid duplicate requests
  const pendingActivityFetches = useRef<Set<string>>(new Set());
  // On-demand fetch for missing activities referenced by chats
  useEffect(() => {
    // Build a set of all known activity IDs (active, inactive, deleted)
    const knownIds = new Set([
      ...activities.map((a) => a.id),
      ...Array.from(allActivitiesById.keys()),
    ]);
    // Find activityIds referenced by chats that are missing
    const missingIds = rawChats
      .map((chat) => chat.activityId)
      .filter(
        (id): id is string =>
          !!id && !knownIds.has(id) && !pendingActivityFetches.current.has(id)
      );
    if (missingIds.length === 0) return;
    missingIds.forEach((id) => {
      pendingActivityFetches.current.add(id);
      refreshSingleActivity(id).finally(() => {
        pendingActivityFetches.current.delete(id);
      });
    });
  }, [rawChats, activities, allActivitiesById, refreshSingleActivity]);

  useEffect(() => {
    // Use allActivitiesById for status derivation, so chats referencing inactive/deleted activities get correct status
    const activityMap = new Map([
      ...activities,
      ...Array.from(allActivitiesById.values()),
    ].map((activity) => [activity.id, activity]));
    const statusMismatches: { chatId: string; activityStatus: "active" | "inactive" }[] = [];

    // Compute derivedChats and mismatches up front
    const previousChats = chats;
    const previousById = new Map(previousChats.map((chat) => [chat.id, chat]));
    const derivedChats = rawChats.map((chat) => {
      const baseStatus = (chat.activityStatus || "unknown") as "active" | "inactive" | "unknown";

      if (!chat.activityId) {
        const directChat =
          chat.activityStatus === "active"
            ? chat
            : { ...chat, activityStatus: "active" as const };
        const previous = previousById.get(chat.id);
        if (previous && areChatsEquivalent(previous, directChat)) {
          return previous;
        }
        return directChat;
      }

      const activity = activityMap.get(chat.activityId) || null;
      const derivedStatus = activity ? deriveActivityStatus(activity) : baseStatus;

      if (
        derivedStatus !== "unknown" &&
        chat.activityStatus !== derivedStatus &&
        chat.activityId
      ) {
        statusMismatches.push({ chatId: chat.id, activityStatus: derivedStatus });
      }

      const nextChat =
        chat.activityStatus === derivedStatus
          ? chat
          : {
              ...chat,
              activityStatus: derivedStatus,
            };
      const previous = previousById.get(chat.id);
      if (previous && areChatsEquivalent(previous, nextChat)) {
        return previous;
      }
      return nextChat;
    });

    if (
      previousChats.length !== derivedChats.length ||
      !previousChats.every((chat, index) => chat === derivedChats[index])
    ) {
      setChats(derivedChats);
    }

    // Persist corrected statuses in the background so future loads are accurate
    if (statusMismatches.length > 0) {
      syncChatActivityStatus({ mismatches: statusMismatches }).catch(
        (error: any) => {
          crashlytics.logError(
            `Error syncing chat activityStatus batch size=${statusMismatches.length}: ${JSON.stringify(error?.message)}`,
            "ActivityContext.tsx",
            error,
          );
        },
      );
    }
  }, [rawChats, activities, allActivitiesById, deriveActivityStatus]);

  const activityById = useMemo(
    () => new Map(activities.map((activity) => [activity.id, activity])),
    [activities],
  );

  const activeActivityIds = useMemo(() => {
    const ids = new Set<string>();
    activities.forEach((activity) => {
      if (deriveActivityStatus(activity) === "active") {
        ids.add(activity.id);
      }
    });
    return ids;
  }, [activities, deriveActivityStatus]);

  const chatById = useMemo(
    () => new Map(chats.map((chat) => [chat.id, chat])),
    [chats],
  );

  const chatsByUserId = useMemo(() => {
    const byUserId = new Map<string, Chat[]>();

    chats.forEach((chat) => {
      chat.participants.forEach((userId) => {
        const existing = byUserId.get(userId);
        if (existing) {
          existing.push(chat);
        } else {
          byUserId.set(userId, [chat]);
        }
      });
    });

    return byUserId;
  }, [chats]);

  const directChatIdByParticipantPair = useMemo(() => {
    const byPair = new Map<string, string>();

    chats.forEach((chat) => {
      if (chat.activityId !== null || chat.participants.length !== 2) return;

      const pairKey = [...chat.participants].sort().join("__");
      byPair.set(pairKey, chat.id);
    });

    return byPair;
  }, [chats]);

  const unreadMessageCountByChatId = useMemo(() => {
    const counts = new Map<string, number>();

    notifications.forEach((notification) => {
      if (notification.type !== "MESSAGE" || notification.isRead) return;
      counts.set(
        notification.chatId,
        (counts.get(notification.chatId) || 0) + 1,
      );
    });

    return counts;
  }, [notifications]);

  const unreadMessageCountByUserAndChatId = useMemo(() => {
    const counts = new Map<string, number>();

    notifications.forEach((notification) => {
      if (notification.type !== "MESSAGE" || notification.isRead) return;

      const key = `${notification.userId}__${notification.chatId}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    return counts;
  }, [notifications]);

  const unreadMessageCountByUserId = useMemo(() => {
    const counts = new Map<string, number>();

    notifications.forEach((notification) => {
      if (notification.type !== "MESSAGE" || notification.isRead) return;

      if (notification.activityId && !activeActivityIds.has(notification.activityId)) {
        return;
      }

      counts.set(
        notification.userId,
        (counts.get(notification.userId) || 0) + 1,
      );
    });

    return counts;
  }, [notifications, activeActivityIds]);

  const unreadGroupChatCountByUserId = useMemo(() => {
    const counts = new Map<string, number>();

    chats.forEach((chat) => {
      if (chat.activityId === null || !activeActivityIds.has(chat.activityId)) {
        return;
      }

      chat.participants.forEach((userId) => {
        const unreadCount =
          unreadMessageCountByUserAndChatId.get(`${userId}__${chat.id}`) || 0;

        if (unreadCount === 0) return;

        counts.set(userId, (counts.get(userId) || 0) + unreadCount);
      });
    });

    return counts;
  }, [chats, activeActivityIds, unreadMessageCountByUserAndChatId]);

  const unreadDirectChatCountByUserId = useMemo(() => {
    const counts = new Map<string, number>();

    chats.forEach((chat) => {
      if (chat.activityId !== null) return;

      chat.participants.forEach((userId) => {
        const unreadCount =
          unreadMessageCountByUserAndChatId.get(`${userId}__${chat.id}`) || 0;

        if (unreadCount === 0) return;

        counts.set(userId, (counts.get(userId) || 0) + unreadCount);
      });
    });

    return counts;
  }, [chats, unreadMessageCountByUserAndChatId]);

  const unreadNonMessageNotificationCountByUserId = useMemo(() => {
    const counts = new Map<string, number>();

    notifications.forEach((notification) => {
      if (notification.type === "MESSAGE" || notification.isRead) return;

      counts.set(
        notification.userId,
        (counts.get(notification.userId) || 0) + 1,
      );
    });

    return counts;
  }, [notifications]);

  const currentUserRef = useRef(currentUser);
  const directChatIdByParticipantPairRef = useRef(directChatIdByParticipantPair);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    directChatIdByParticipantPairRef.current = directChatIdByParticipantPair;
  }, [directChatIdByParticipantPair]);

  // UTILITY/GETTER FUNCTIONS
  const getChatsForUser = useCallback(
    (userId: string) => chatsByUserId.get(userId) || [],
    [chatsByUserId],
  );
  const getChatById = useCallback(
    (chatId: string) => chatById.get(chatId),
    [chatById],
  );
  const getDirectChatId = useCallback(
    (otherUserId: string) => {
      if (!currentUser) return undefined;
      return directChatIdByParticipantPair.get(
        [currentUser.id, otherUserId].sort().join("__"),
      );
    },
    [directChatIdByParticipantPair, currentUser],
  );

  const createOrGetDirectChat = useCallback(
    async (otherUserId: string): Promise<string> => {
      const activeUser = currentUserRef.current;
      if (!activeUser) throw new Error("User not authenticated");

      // First, try to find existing chat
      const existingChatId = directChatIdByParticipantPairRef.current.get(
        [activeUser.id, otherUserId].sort().join("__"),
      );

      if (existingChatId) {
        return existingChatId;
      }

      if (!db) return "";

      // Create new direct chat
      const chatRef = doc(collection(db, "chats"));
      const newChat = {
        activityId: null,
        activityTitle: "Direct Chat",
        participants: [activeUser.id, otherUserId],
        leftParticipantIds: [],
        messages: [],
      };

      await setDoc(chatRef, newChat);

      // Notify the other user about the new direct chat
      // Use setTimeout to avoid dependency cycle
      setTimeout(async () => {
        const latestUser = currentUserRef.current;
        if (latestUser) {
          if (!db) return;
          try {
            await addDoc(collection(db, "notifications"), {
              userId: otherUserId,
              type: "DIRECT_CHAT",
              text: `<strong>${latestUser.username}</strong> started a chat with you`,
              link: `/chat/${chatRef.id}`,
              chatId: chatRef.id,
              isRead: false,
              fromUserId: latestUser.id,
              createdAt: serverTimestamp(),
            });
          } catch (err: any) {
            // If error is already-exists, ignore, else rethrow
            if (err?.code !== "already-exists") {
              throw err;
            }
          }
        }
      }, 0);

      return chatRef.id;
    },
    [],
  );

  const getUnreadMessageCountForChat = useCallback(
    (chatId: string) => unreadMessageCountByChatId.get(chatId) || 0,
    [unreadMessageCountByChatId],
  );

  const getUnreadMessageCount = useCallback(
    (userId: string) => {
      if (!userId) return 0;
      return unreadMessageCountByUserId.get(userId) || 0;
    },
    [unreadMessageCountByUserId],
  );

  const getUnreadGroupChatCount = useCallback(
    (userId: string) => {
      if (!userId) return 0;
      return unreadGroupChatCountByUserId.get(userId) || 0;
    },
    [unreadGroupChatCountByUserId],
  );

  const getUnreadDirectChatCount = useCallback(
    (userId: string) => {
      if (!userId) return 0;
      return unreadDirectChatCountByUserId.get(userId) || 0;
    },
    [unreadDirectChatCountByUserId],
  );

  const getUnreadNotificationCount = useCallback(
    (userId: string) => unreadNonMessageNotificationCountByUserId.get(userId) || 0,
    [unreadNonMessageNotificationCountByUserId],
  );

  const getAuditLogsForUser = useCallback(
    (userId: string) => auditLogs.filter((l) => l.userId === userId),
    [auditLogs],
  );

  const getAuditLogsForActivity = useCallback(
    (activityId: string) => {
      // Return all audit logs for this activity, regardless of context paging or participant status
      const activityRelatedTypes: AuditLog["type"][] = [
        "ACTIVITY_CREATE",
        "ACTIVITY_JOIN",
        "ACTIVITY_LEAVE",
        "ACTIVITY_PASS",
        "ACTIVITY_DELETE",
        "ACTIVITY_RESTORE",
        "ACTIVITY_UPDATE",
        "ACTIVITY_KICK_PARTICIPANT",
        "ACTIVITY_KICKED",
      ];
      return auditLogs.filter(
        (l) =>
          l.link === `/activity/${activityId}` &&
          activityRelatedTypes.includes(l.type),
      );
    },
    [auditLogs],
  );

  // DATABASE-MODIFYING FUNCTIONS
  // Cache to prevent duplicate audit log creation
  const recentAuditLogsCache = useRef(new Map<string, number>());
  // Cache to prevent duplicate notification creation
  const recentNotificationsCache = useRef(new Map<string, number>());

  const createAuditLog = useCallback(
    async (
      type: AuditLog["type"],
      text: string,
      link: string,
      fromUserId: string,
    ) => {
      if (!currentUser) return;

      // Prevent duplicate audit logs using cache
      if (type === "ACTIVITY_UPDATE") {
        const cacheKey = `${type}:${text}:${link}:${fromUserId}:${currentUser.id}`;
        const now = Date.now();
        const lastCreated = recentAuditLogsCache.current.get(cacheKey);

        // If an identical audit log was created within the last 10 seconds, skip it
        if (lastCreated && now - lastCreated < 10000) {
          return;
        }

        // Update cache with current timestamp
        recentAuditLogsCache.current.set(cacheKey, now);

        // Clean up old cache entries (older than 1 minute)
        for (const [key, timestamp] of recentAuditLogsCache.current.entries()) {
          if (now - timestamp > 60000) {
            recentAuditLogsCache.current.delete(key);
          }
        }
      }

      if (!db) return;
      try {
        await addDoc(collection(db, "auditLogs"), {
          type,
          text,
          link,
          fromUserId,
          userId: currentUser.id,
          createdAt: serverTimestamp(),
          activityId:
            link && link.startsWith("/activity/")
              ? link.split("/activity/")[1]
              : "",
        });
      } catch (err: any) {
        // If error is already-exists, ignore, else rethrow
        if (err?.code !== "already-exists") {
          throw err;
        }
      }
    },
    [currentUser],
  );

  const createNotification = useCallback(
    async (
      targetUserId: string,
      type: Notification["type"],
      text: string,
      link: string,
      chatId: string = "",
      activityId?: string,
    ) => {
      if (!currentUser) return;

      // Prevent duplicate ACTIVITY_UPDATE notifications using cache
      if (type === "ACTIVITY_UPDATE" && activityId) {
        const cacheKey = `${type}:${targetUserId}:${activityId}:${currentUser.id}`;
        const now = Date.now();
        const lastCreated = recentNotificationsCache.current.get(cacheKey);

        // If an identical notification was created within the last 5 seconds, skip it
        if (lastCreated && now - lastCreated < 5000) {
          return;
        }

        // Update cache with current timestamp
        recentNotificationsCache.current.set(cacheKey, now);

        // Clean up old cache entries (older than 1 minute)
        for (const [
          key,
          timestamp,
        ] of recentNotificationsCache.current.entries()) {
          if (now - timestamp > 60000) {
            recentNotificationsCache.current.delete(key);
          }
        }
      }

      // alert(`Creating notification: ${type} for user ${targetUserId} currentUser: ${currentUser.id}`);

      // Check for existing similar unread notification to group them
      // Only group certain notification types that make sense to be grouped
      // Note: ACTIVITY_UPDATE is excluded to show each update as a separate notification
      const groupableTypes: Notification["type"][] = [
        "MESSAGE",
        "JOIN",
        "LEAVE",
        "NEW_STORY",
      ];

      if (groupableTypes.includes(type)) {
        if (!db) return;
        // Query for similar unread notifications
        const notifQuery = query(
          collection(db, "notifications"),
          where("userId", "==", targetUserId),
          where("type", "==", type),
          where("fromUserId", "==", currentUser.id),
          where("isRead", "==", false),
          ...(chatId ? [where("chatId", "==", chatId)] : []),
          ...(activityId ? [where("activityId", "==", activityId)] : []),
          orderBy("createdAt", "desc"),
          limit(1),
        );

        const existingNotifs = await getDocs(notifQuery);

        if (!existingNotifs.empty) {
          // Update existing notification with incremented count
          const existingNotif = existingNotifs.docs[0];
          const existingData = existingNotif.data();
          const currentCount = existingData.count || 1;

          await updateDoc(doc(db, "notifications", existingNotif.id), {
            count: currentCount + 1,
            createdAt: serverTimestamp(), // Update timestamp to keep it at top
            text: text, // Update text to reflect latest action
          });

          // Note: Local notifications removed - push notifications handle display

          return; // Exit early, notification grouped
        }
      }

      // Create new notification if no grouping occurred
      if (!db) return;
      try {
        await addDoc(collection(db, "notifications"), {
          userId: targetUserId,
          type,
          text,
          link,
          chatId,
          activityId: activityId || "",
          isRead: false,
          fromUserId: currentUser.id,
          count: 1,
          createdAt: serverTimestamp(),
        });
      } catch (err: any) {
        // If error is already-exists, ignore, else rethrow
        if (err?.code !== "already-exists") {
          throw err;
        }
      }
      // Note: Local notifications removed - push notifications handle all notification display
      // Push notifications are sent via Cloud Function when notification document is created
    },
    [currentUser],
  );

  const createUserJoinedAuditLog = useCallback(async () => {
    if (!currentUser) return;

    // Create an audit log for when the user joined the app
    if (!db) return;
    try {
      await addDoc(collection(db, "auditLogs"), {
        type: "USER_JOINED_APP" as AuditLogType,
        text: `Welcome to Who's in?! ${currentUser.username} joined the community and is ready to discover amazing activities.`,
        link: "/foryou", // Link to their For You page
        fromUserId: currentUser.id,
        userId: currentUser.id,
        createdAt: serverTimestamp(),
      });
    } catch (err: any) {
      // If error is already-exists, ignore, else rethrow
      if (err?.code !== "already-exists") {
        throw err;
      }
    }
    // Log user join to Analytics
    analytics.logEvent("wii_user_joined_app", {
      id: currentUser.id,
      username: currentUser.username,
      joinedAt: new Date().toISOString(),
      timestamp: new Date().toISOString(),
    });
    crashlytics.log(
      `User Joined App: ${currentUser.username} (${currentUser.id})`,
    );
  }, [currentUser]);

  const createActivity = useCallback(
    async (
      newActivityData: Omit<
        ActivityRequest,
        | "id"
        | "creator"
        | "currentSize"
        | "isActive"
        | "createdAt"
        | "participants"
        | "chatId"
        | "reason"
        | "isDeleted"
        | "deletedAt"
      > & { locationAddress?: string },
    ) => {
      if (!currentUser) return;

      const createStartedAt = Date.now();

      try {
        // Call Cloud Function to create activity (handles all DB operations)
        const callableStartedAt = Date.now();
        const response = await createActivityCF({
          title: newActivityData.title,
          description: newActivityData.description || undefined,
          category: newActivityData.category,
          duration: newActivityData.duration,
          groupSize: newActivityData.groupSize,
          dateTime: newActivityData.dateTime,
          location: newActivityData.location,
          locationAddress: newActivityData.locationAddress,
          coordinates: newActivityData.coordinates,
          imageUrl: newActivityData.imageUrl || undefined,
          isPrivate: newActivityData.isPrivate,
        });
        const callableDurationMs = Date.now() - callableStartedAt;
        const totalDurationMs = Date.now() - createStartedAt;

        const activityId = response.activityId;

        analytics.logEvent("wii_activity_create_timing", {
          userId: currentUser.id,
          activityId,
          callableDurationMs,
          totalDurationMs,
          timestamp: new Date().toISOString(),
        });
        crashlytics.log(
          `Activity Create Timing (client): callable=${callableDurationMs}ms total=${totalDurationMs}ms activityId=${activityId}`,
        );

        // Log activity creation to Analytics
        analytics.logEvent("wii_activity_created", {
          id: activityId,
          title: newActivityData.title,
          createdBy: currentUser.id,
          createdAt: newActivityData.dateTime,
          category: newActivityData.category,
          groupSize: newActivityData.groupSize,
          duration: newActivityData.duration,
          timestamp: new Date().toISOString(),
        });
        crashlytics.log(
          `Activity Created: ${newActivityData.title} (${activityId}) by ${currentUser.username} (${currentUser.id})`,
        );

        // Queue action for profile update (batched, not immediate) with full activity details
        queueUserAction(
          "create",
          activityId,
          newActivityData.title,
          newActivityData.category,
          newActivityData.location,
          {
            description: newActivityData.description,
            duration: newActivityData.duration,
            groupSize: newActivityData.groupSize,
            dateTime: newActivityData.dateTime,
            source: "activity_creation",
          },
        ).catch((err) =>
          crashlytics.logError(
            `Error queueing user action create for activity ${activityId}: ${JSON.stringify(err?.message)}`,
            "ActivityContext.tsx",
            err,
          ),
        );

        addToast(
          `Activity "${newActivityData.title}" created successfully!`,
          "success",
        );
        return activityId;
      } catch (error) {
        const totalDurationMs = Date.now() - createStartedAt;
        crashlytics.logError(
          `Error creating activity after ${totalDurationMs}ms: ${JSON.stringify(error)}`,
          "ActivityContext.tsx",
          error,
        );
        addToast("Failed to create activity.", "error");
        return undefined;
      }
    },
    [currentUser, queueUserAction, addToast],
  );

  const markInviteNotificationsAsRead = useCallback(
    async (activityId: string) => {
      if (!db || !currentUser) return;

      const inviteNotifications = await getDocs(
        query(
          collection(db, "notifications"),
          where("userId", "==", currentUser.id),
          where("type", "==", "ACTIVITY_INVITE"),
          where("activityId", "==", activityId),
          where("isRead", "==", false),
        ),
      );

      if (inviteNotifications.empty) return;

      const notificationBatch = writeBatch(db);
      inviteNotifications.docs.forEach((docSnap) => {
        notificationBatch.update(docSnap.ref, { isRead: true });
      });
      await notificationBatch.commit();
    },
    [currentUser],
  );

  const joinActivity = useCallback(
    async (activityId: string) => {
      if (!currentUser) {
        addToast("Failed to join the activity.", "error");
        return { success: false, isFull: false, isRequestSent: false };
      }

      const existingActivity = activities.find((a) => a.id === activityId);
      if (!existingActivity) {
        addToast("Failed to join the activity.", "error");
        return { success: false, isFull: false, isRequestSent: false };
      }

      // If already a participant, treat as success (no-op)
      if (existingActivity.participants.some((p) => p.id === currentUser.id)) {
        return { success: true, isFull: existingActivity.isFull, isRequestSent: false };
      }

      // Optimistic update: add current user locally
      const optimisticActivity: ActivityRequest = {
        ...existingActivity,
        participants: [...existingActivity.participants, currentUser],
        currentSize: (existingActivity.currentSize || existingActivity.participants.length) + 1,
        isFull:
          ((existingActivity.currentSize || existingActivity.participants.length) + 1) >=
          existingActivity.groupSize,
      };

      setActivities((prev) =>
        prev.map((a) => (a.id === activityId ? optimisticActivity : a)),
      );

      try {
        // Call Cloud Function to join activity (handles all validation and DB operations)
        const response = await joinActivityCF({ activityId });

        if (!response.success) {
          // Roll back optimistic state
          setActivities((prev) =>
            prev.map((a) => (a.id === activityId ? existingActivity : a)),
          );
          if (response.isFull) {
            addToast("Activity is full.", "info");
          } else {
            addToast("Failed to join the activity.", "error");
          }
          return {
            success: false,
            isFull: response.isFull,
            isRequestSent: false,
          };
        }

        // Use optimistic activity for metadata/logging
        const activity = optimisticActivity;
        if (activity) {
          // Queue action for profile update (batched, not immediate)
          queueUserAction(
            "join",
            activityId,
            activity.title,
            activity.category,
            activity.location,
            {
              duration: activity.duration,
              groupSize: activity.groupSize,
              dateTime: activity.dateTime,
              currentSize: activity.currentSize + 1,
              joinedWhenAlmostFull: response.isFull,
            },
          ).catch((err) =>
            crashlytics.logError(
              `Error queueing user action join for activity ${activityId}: ${JSON.stringify(err?.message)}`,
              "ActivityContext.tsx",
              err,
            ),
          );

          // Log activity join to Analytics
          analytics.logEvent("wii_activity_joined", {
            id: activityId,
            title: activity.title,
            createdBy: activity.creator.id,
            category: activity.category,
            groupSize: activity.groupSize,
            duration: activity.duration,
            joinedBy: currentUser.id,
            isFull: response.isFull,
            timestamp: new Date().toISOString(),
          });
          crashlytics.log(
            `Activity Joined: ${activity.title} (${activityId}) by ${currentUser.username} (${currentUser.id})`,
          );
        }

        if (response.isFull) {
          addToast(`You joined the activity. It's now full! 🎉`, "success");
        } else {
          addToast(`You've joined the activity!`, "success");
        }

        // Remove joined activity from Home feed immediately
        setActivities((prev) => prev.filter((a) => a.id !== activityId));

        return { success: true, isFull: response.isFull, isRequestSent: false };
      } catch (error) {
        // Roll back optimistic state
        setActivities((prev) =>
          prev.map((a) => (a.id === activityId ? existingActivity : a)),
        );
        crashlytics.logError(
          `Error joining activity ${activityId}: ${JSON.stringify(error)}`,
          "ActivityContext.tsx",
          error,
        );
        const notFound = (error as any)?.code === "not-found";
        if (notFound) {
          // Drop from feed if it no longer exists server-side
          setActivities((prev) => prev.filter((a) => a.id !== activityId));
          setAllActivityIds((prev) => {
            const next = new Set(prev);
            next.delete(activityId);
            return next;
          });
          addToast("This activity is no longer available.", "info");
        } else {
          addToast("Failed to join the activity.", "error");
        }
        return { success: false, isFull: false, isRequestSent: false };
      }
    },
    [currentUser, activities, queueUserAction, addToast],
  );

  const passActivity = useCallback(
    async (activityId: string) => {
      if (!currentUser) return;

      const activity = activities.find((a) => a.id === activityId);
      if (!activity) return;

      // Optimistically remove from local feed for snappier UX
      setActivities((prev) => prev.filter((a) => a.id !== activityId));
      setAllActivityIds((prev) => {
        const next = new Set(prev);
        next.delete(activityId);
        return next;
      });

      try {
        // Call Cloud Function to pass activity (handles all validation and DB operations)
        const response = await passActivityCF({ activityId });

        if (!response.success) {
          setActivities((prev) => {
            const exists = prev.some((a) => a.id === activityId);
            return exists ? prev : [activity, ...prev];
          });
          setAllActivityIds((prev) => {
            const next = new Set(prev);
            next.add(activityId);
            return next;
          });
          addToast("Failed to pass on activity.", "error");
          return;
        }

        // Mark invite notifications as read if user had a pending invite
        if (activity?.pendingInvites?.includes(currentUser.id)) {
          await markInviteNotificationsAsRead(activityId);
        }

        // Queue user action for profile update
        if (activity) {
          await queueUserAction(
            "pass",
            activityId,
            activity.title,
            activity.category,
            activity.location,
          );

          // Log activity pass to Analytics
          analytics.logEvent("wii_activity_passed", {
            id: activityId,
            title: activity.title,
            createdBy: activity.creator.id,
            category: activity.category,
            groupSize: activity.groupSize,
            duration: activity.duration,
            passedBy: currentUser.id,
            timestamp: new Date().toISOString(),
          });
          crashlytics.log(
            `Activity Passed: ${activity.title} (${activityId}) by ${currentUser.username} (${currentUser.id})`,
          );
        }
      } catch (error) {
        setActivities((prev) => {
          const exists = prev.some((a) => a.id === activityId);
          return exists ? prev : [activity, ...prev];
        });
        setAllActivityIds((prev) => {
          const next = new Set(prev);
          next.add(activityId);
          return next;
        });
        crashlytics.logError(
          `Error passing activity ${activityId}: ${JSON.stringify(error)}`,
          "ActivityContext.tsx",
          error,
        );
        const notFound = (error as any)?.code === "not-found";
        if (notFound) {
          // Already optimistically removed; keep it out of the feed
          addToast("This activity is no longer available.", "info");
        } else {
          addToast("Failed to pass on activity. Please try again.", "error");
        }
      }
    },
    [currentUser, activities, markInviteNotificationsAsRead, addToast],
  );

  const sendMessage = useCallback(
    async (
      chatId: string,
      text: string,
      replyTo?: {
        messageId: string;
        senderId: string;
        senderUsername: string;
        text: string;
      },
      options?: {
        format?: "plain" | "whatsapp";
        media?: ChatMediaAttachment[];
        location?: ChatLocationPayload;
        isForwarded?: boolean;
        clientRequestId?: string;
      },
    ) => {
      if (!currentUser) return;

      try {
        // Call Cloud Function to send message and create notifications
        const response = await sendChatMessage({
          chatId,
          clientRequestId: options?.clientRequestId,
          text,
          format: options?.format || "whatsapp",
          media: options?.media || [],
          location: options?.location || null,
          replyTo: replyTo || null,
          isForwarded: options?.isForwarded === true,
        });

        if (!response.success) {
          throw new Error("Failed to send message");
        }

        // Queue user action for profile update (chat engagement)
        const chat = chats.find((c) => c.id === chatId);
        await queueUserAction(
          "message",
          chatId,
          chat?.activityTitle || "Direct Chat",
          undefined,
          undefined,
          {
            chatType: chat?.activityId ? "group" : "direct",
            isReply: !!replyTo,
            hasMedia: (options?.media?.length || 0) > 0,
            mediaCount: options?.media?.length || 0,
            hasLocation: Boolean(options?.location),
            locationKind: options?.location?.kind || null,
            format: options?.format || "whatsapp",
          },
        );

        // Log analytics event
        analytics.logEvent("wii_chat_message_sent", {
          chatId,
          messageId: response.messageId,
          hasReply: !!replyTo,
          hasMedia: (options?.media?.length || 0) > 0,
          mediaCount: options?.media?.length || 0,
          hasLocation: Boolean(options?.location),
          locationKind: options?.location?.kind || null,
          format: options?.format || "whatsapp",
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        crashlytics.logError(
          `Error sending message in chat ${chatId}: ${JSON.stringify(error)}`,
          "ActivityContext.tsx",
          error,
        );
        throw error;
      }
    },
    [currentUser, chats],
  );

  const toggleStarMessage = useCallback(
    async (chatId: string, messageId: string): Promise<boolean> => {
      if (!currentUser) throw new Error("User must be authenticated");

      const response = await toggleStarMessageCF({
        chatId,
        messageId,
      });

      if (!response.success) {
        throw new Error("Failed to toggle star message");
      }

      return response.starred;
    },
    [currentUser],
  );

  const forwardMessages = useCallback(
    async (
      sourceChatId: string,
      messageIds: string[],
      targetChatIds: string[],
    ): Promise<number> => {
      if (!currentUser) throw new Error("User must be authenticated");

      const response = await forwardMessagesCF({
        sourceChatId,
        messageIds,
        targetChatIds,
      });

      if (!response.success) {
        throw new Error("Failed to forward messages");
      }

      return response.forwardedCount;
    },
    [currentUser],
  );

  const markMessageAsDelivered = useCallback(
    async (chatId: string, messageId: string) => {
      if (!db || !currentUser) return;

      const chat = chats.find((c) => c.id === chatId);
      if (!chat) return;

      const message = chat.messages.find((m) => m.id === messageId);
      if (!message || message.senderId === currentUser.id) return; // Don't mark own messages

      // Check if already delivered
      if (message.deliveredTo?.includes(currentUser.id)) return;

      try {
        const timestamp = new Date().toISOString();
        await setDoc(
          doc(db, "chatMeta", chatId),
          {
            receipts: {
              [messageId]: {
                deliveredTo: {
                  [currentUser.id]: timestamp,
                },
              },
            },
            updatedAt: timestamp,
          },
          { merge: true },
        );
      } catch (error) {
        crashlytics.logError(
          `Error marking message as delivered ${messageId} in chat ${chatId}: ${JSON.stringify(error)}`,
          "ActivityContext.tsx",
          error,
        );
      }
    },
    [currentUser, chats],
  );

  const markMessageAsRead = useCallback(
    async (chatId: string, messageId: string) => {
      if (!db || !currentUser) return;

      const chat = chats.find((c) => c.id === chatId);
      if (!chat) return;

      const message = chat.messages.find((m) => m.id === messageId);
      if (!message || message.senderId === currentUser.id) return; // Don't mark own messages

      // Check if already read
      if (message.readBy?.includes(currentUser.id)) return;

      try {
        const timestamp = new Date().toISOString();
        await setDoc(
          doc(db, "chatMeta", chatId),
          {
            receipts: {
              [messageId]: {
                readBy: {
                  [currentUser.id]: timestamp,
                },
                deliveredTo: {
                  [currentUser.id]: timestamp,
                },
              },
            },
            updatedAt: timestamp,
          },
          { merge: true },
        );
      } catch (error) {
        crashlytics.logError(
          `Error marking message as read ${messageId} in chat ${chatId}: ${JSON.stringify(error)}`,
          "ActivityContext.tsx",
          error,
        );
      }
    },
    [currentUser, chats],
  );

  const addReactionToMessage = useCallback(
    async (chatId: string, messageId: string, emoji: string) => {
      if (!currentUser) return;

      const chat = chats.find((c) => c.id === chatId);
      if (!chat) return;

      const message = chat.messages.find((m) => m.id === messageId);
      if (!message) return;

      try {
        // Call Cloud Function to add/update reaction
        const response = await updateMessageReaction({
          chatId,
          messageId,
          emoji,
          action: "add",
        });

        if (!response.success) {
          throw new Error("Failed to add reaction");
        }

        // Queue user action for profile update (engagement behavior)
        await queueUserAction(
          "message_reaction",
          chatId,
          chat?.activityTitle || "Chat",
          undefined,
          undefined,
          {
            chatType: chat?.activityId ? "group" : "direct",
          },
        );

        // Log analytics event
        analytics.logEvent("wii_message_reaction_added", {
          chatId,
          messageId,
          emoji,
          reactorCount: response.reactors.length,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        crashlytics.logError(
          `Error adding reaction to message ${messageId} in chat ${chatId}: ${JSON.stringify(error)}`,
          "ActivityContext.tsx",
          error,
        );
        throw error;
      }
    },
    [currentUser, chats],
  );

  const editMessage = useCallback(
    async (
      chatId: string,
      messageId: string,
      text: string,
      format: "plain" | "whatsapp" = "whatsapp",
    ) => {
      if (!currentUser) return;

      try {
        const response = await updateChatMessage({
          chatId,
          messageId,
          action: "edit",
          text,
          format,
        });

        if (!response.success) {
          throw new Error("Failed to edit message");
        }

        analytics.logEvent("wii_chat_message_edited", {
          chatId,
          messageId,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        crashlytics.logError(
          `Error editing message ${messageId} in chat ${chatId}: ${JSON.stringify(error)}`,
          "ActivityContext.tsx",
          error,
        );
        throw error;
      }
    },
    [currentUser],
  );

  const deleteMessage = useCallback(
    async (
      chatId: string,
      messageId: string,
      mode: "for_me" | "for_everyone",
    ) => {
      if (!currentUser) return;

      try {
        const response = await updateChatMessage({
          chatId,
          messageId,
          action: mode === "for_everyone" ? "delete_for_everyone" : "delete_for_me",
        });

        if (!response.success) {
          throw new Error("Failed to delete message");
        }

        analytics.logEvent("wii_chat_message_deleted", {
          chatId,
          messageId,
          mode,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        crashlytics.logError(
          `Error deleting message ${messageId} in chat ${chatId}: ${JSON.stringify(error)}`,
          "ActivityContext.tsx",
          error,
        );
        throw error;
      }
    },
    [currentUser],
  );

  // Shared in-progress promise for fetchRecommendations
  const fetchRecommendationsPromiseRef = useRef<Promise<boolean> | null>(null);

  const fetchRecommendations = useCallback(async (): Promise<boolean> => {
    if (fetchRecommendationsPromiseRef.current) {
      return fetchRecommendationsPromiseRef.current;
    }
    let didFetch = false;
    if (!currentUser || isLoadingRecommendations) return didFetch;

    // Create cache key based on user activities and passed activities
    const userActivityCount = activities.filter(
      (a) =>
        !a.isDeleted && a.participants.some((p) => p.id === currentUser.id),
    ).length;
    const availableActivityCount = activities.filter(
      (a) =>
        !a.isDeleted &&
        !a.isPrivate && // Exclude private activities from available count
        new Date(a.dateTime) > new Date() &&
        toJsDate(a.dateTime) > new Date() &&
        a.creator.id !== currentUser.id &&
        !a.participants.some((p) => p.id === currentUser.id),
    ).length;
    const cacheKey = `${currentUser.id}-${userActivityCount}-${availableActivityCount}-${passedActivityIds.length}`;

    // Check if we have fresh cached data (within 5 minutes)
    const now = Date.now();
    const cacheExpiry = 5 * 60 * 1000; // 5 minutes
    if (
      recommendationsCacheKey === cacheKey &&
      now - lastRecommendationFetch < cacheExpiry &&
      (recommendations.length > 0 || vibeCheckUsers.length > 0)
    ) {
      return didFetch; // Use cached data
    }

    setIsLoadingRecommendations(true);
    // Create a shared promise
    fetchRecommendationsPromiseRef.current = (async () => {
      let fetchedRecommendationCount = 0;
      let fetchedVibeCheckUserCount = 0;
      let receivedRecommendationChunk = false;

      try {
        const response = await getForYouRecommendationsCF();

        const recommendationPayloads = Array.isArray(response.recommendations)
          ? response.recommendations
          : [];

        const hydratedRecommendations = await contextHydrateActivities(
          recommendationPayloads,
        );

        const reasonById = new Map<string, string>();
        recommendationPayloads.forEach((item: any) => {
          if (item?.id && typeof item.reason === "string") {
            reasonById.set(item.id, item.reason);
          }
        });

        // Filter out duplicate activities by id
        const uniqueRecommendations: (ActivityRequest & { reason: string })[] = [];
        const seenIds = new Set<string>();
        hydratedRecommendations.forEach((activity) => {
          if (seenIds.has(activity.id)) return;
          seenIds.add(activity.id);
          uniqueRecommendations.push({
            ...activity,
            reason: reasonById.get(activity.id) || "Recommended for you",
          });
        });

        const safeVibeChecks = Array.isArray(response.vibeCheckUsers)
          ? response.vibeCheckUsers
          : [];

        fetchedRecommendationCount = uniqueRecommendations.length;
        fetchedVibeCheckUserCount = safeVibeChecks.length;
        receivedRecommendationChunk =
          uniqueRecommendations.length > 0 || safeVibeChecks.length > 0;

        setRecommendations(uniqueRecommendations);
        setVibeCheckUsers(safeVibeChecks as VibeCheckUser[]);

        // Update cache info
        setLastRecommendationFetch(now);
        setRecommendationsCacheKey(cacheKey);
        didFetch = receivedRecommendationChunk;
      } catch (error) {
        crashlytics.logError(
          `Error fetching recommendations: ${JSON.stringify(error)}`,
          "ActivityContext.tsx",
          error,
        );
        fetchedRecommendationCount = 0;
        fetchedVibeCheckUserCount = 0;
        setRecommendations([]);
        setVibeCheckUsers([]);
      } finally {
        setIsLoadingRecommendations(false);
        fetchRecommendationsPromiseRef.current = null;
      }
      // Log analytics event for fetching recommendations
      analytics.logEvent("wii_recommendations_fetched", {
        userId: currentUser.id,
        recommendationCount: fetchedRecommendationCount,
        vibeCheckUserCount: fetchedVibeCheckUserCount,
        timestamp: new Date().toISOString(),
      });
      crashlytics.log(
        `Recommendations Fetched: ${didFetch} with ${fetchedRecommendationCount} recommendations and ${fetchedVibeCheckUserCount} vibe check users for ${currentUser.username} (${currentUser.id})`,
      );
      return didFetch;
    })();
    return fetchRecommendationsPromiseRef.current;
  }, [
    currentUser,
    activities,
    passedActivityIds,
    isLoadingRecommendations,
    lastRecommendationFetch,
    recommendationsCacheKey,
    recommendations.length,
    vibeCheckUsers.length,
    contextHydrateActivities,
  ]);

  // Prefetch ideas for CreatePage - similar to fetchRecommendations but for AI-generated activity ideas
  const prefetchIdeas = useCallback(async () => {
    if (!currentUser || prefetchedIdeas) return; // Don't prefetch if already have ideas

    try {
      // Get user's recent activity history for personalization
      const userRecentActivities = activities
        .filter(
          (a) =>
            !a.isDeleted && a.participants.some((p) => p.id === currentUser.id),
        )
        .slice(0, 20); // Limit to 20 recent activities

      // Generate generic personalized query based on user activity history
      const query =
        userRecentActivities.length > 0
          ? "activities and events that match my interests"
          : "fun activities and events to do";

      const response = await generateActivitySearchIdeasCF({
        query,
        user: currentUser,
        userActivities: userRecentActivities,
        searchHistory: [], // No search history for prefetched ideas
      });

      setPrefetchedIdeas(response);

      // Log analytics event
      analytics.logEvent("wii_ideas_prefetched", {
        userId: currentUser.id,
        ideasCount: prefetchedIdeas?.ideas?.length || 0,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      crashlytics.logError(
        `Error prefetching ideas: ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
      // Silently fail - user can still search manually
    }
  }, [currentUser, activities, prefetchedIdeas]);

  // Implement other methods
  const updateActivity = async (updatedActivity: ActivityRequest) => {
    if (!currentUser || updatedActivity.isDeleted) {
      throw new Error("Cannot update a deleted activity");
    }

    try {
      // Find the original activity for comparison (for analytics and metadata)
      const originalActivityFromState = activities.find(
        (a) => a.id === updatedActivity.id,
      );
      const originalActivity = originalActivityFromState || updatedActivity;
      if (!originalActivityFromState) {
        crashlytics.log(
          `updateActivity fallback: original activity ${updatedActivity.id} missing from local cache`,
        );
      }

      // Prepare update payload
      const participantIds = updatedActivity.participants.map((p) => p.id);
      const updatePayload = {
        id: updatedActivity.id,
        title: updatedActivity.title,
        description: updatedActivity.description || undefined,
        location: updatedActivity.location,
        category: updatedActivity.category,
        dateTime: updatedActivity.dateTime,
        duration: updatedActivity.duration,
        groupSize: updatedActivity.groupSize,
        imageUrl: updatedActivity.imageUrl || undefined,
        participantIds,
        isPrivate: updatedActivity.isPrivate,
      };

      // Call Cloud Function to update activity (handles all validation and DB operations)
      const response = await updateActivityCF(updatePayload);

      if (!response.success) {
        addToast("Failed to update activity.", "error");
        return;
      }

      // Track what changed to understand evolving preferences
      const changedFields: Record<string, any> = {};
      if (originalActivity.title !== updatedActivity.title)
        changedFields.title = true;
      if (originalActivity.description !== updatedActivity.description)
        changedFields.description = true;
      if (originalActivity.location !== updatedActivity.location)
        changedFields.location = true;
      if (originalActivity.category !== updatedActivity.category)
        changedFields.category = true;
      if (
        toJsDate(originalActivity.dateTime).getTime() !==
        toJsDate(updatedActivity.dateTime).getTime()
      )
        changedFields.dateTime = true;
      if (originalActivity.duration !== updatedActivity.duration)
        changedFields.duration = true;
      if (originalActivity.groupSize !== updatedActivity.groupSize)
        changedFields.groupSize = true;
      if (originalActivity.isPrivate !== updatedActivity.isPrivate)
        changedFields.isPrivate = true;

      // Queue user action for profile update with metadata showing what changed
      await queueUserAction(
        "update",
        updatedActivity.id,
        updatedActivity.title,
        updatedActivity.category,
        updatedActivity.location,
        {
          changedFields,
          newDuration: changedFields.duration
            ? updatedActivity.duration
            : undefined,
          newGroupSize: changedFields.groupSize
            ? updatedActivity.groupSize
            : undefined,
          newDateTime: changedFields.dateTime
            ? updatedActivity.dateTime
            : undefined,
        },
      );

      // Generate change description for analytics
      const changes: string[] = [];
      if (originalActivity.title !== updatedActivity.title)
        changes.push("title");
      if (originalActivity.description !== updatedActivity.description)
        changes.push("description");
      if (originalActivity.location !== updatedActivity.location)
        changes.push("location");
      if (
        toJsDate(originalActivity.dateTime).getTime() !==
        toJsDate(updatedActivity.dateTime).getTime()
      )
        changes.push("dateTime");
      if (originalActivity.duration !== updatedActivity.duration)
        changes.push("duration");
      if (originalActivity.groupSize !== updatedActivity.groupSize)
        changes.push("groupSize");
      if (originalActivity.category !== updatedActivity.category)
        changes.push("category");
      if (originalActivity.imageUrl !== updatedActivity.imageUrl)
        changes.push("imageUrl");
      if (originalActivity.isPrivate !== updatedActivity.isPrivate)
        changes.push("isPrivate");

      // Log activity update to Analytics
      analytics.logEvent("wii_activity_updated", {
        id: updatedActivity.id,
        title: updatedActivity.title,
        createdBy: updatedActivity.creator.id,
        category: updatedActivity.category,
        groupSize: updatedActivity.groupSize,
        duration: updatedActivity.duration,
        updatedBy: currentUser.id,
        timestamp: new Date().toISOString(),
        changes: changes,
      });
      crashlytics.log(
        `Activity Updated: ${updatedActivity.title} (${updatedActivity.id}) by ${currentUser.username} (${currentUser.id}) Changes: ${changes.join("; ")}`,
      );

      addToast("Activity updated successfully!", "success");
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : JSON.stringify(error);
      crashlytics.logError(
        `Error updating activity ${updatedActivity.id}: ${errorMessage}`,
        "ActivityContext.tsx",
        error,
      );
      addToast("Failed to update activity. Please try again.", "error");
      throw error;
    }
  };

  const deleteActivity = async (activityId: string) => {
    if (!currentUser) return;

    const existingActivity = activities.find((a) => a.id === activityId);
    const optimisticActivity = existingActivity
      ? {
          ...existingActivity,
          isDeleted: true,
          isActive: false,
          deletedAt: new Date().toISOString(),
        }
      : null;

    if (optimisticActivity) {
      setActivities((prev) =>
        prev.map((activity) =>
          activity.id === activityId ? optimisticActivity : activity,
        ),
      );
    }

    try {
      // Call Cloud Function to delete activity (handles all validation and DB operations)
      const response = await deleteActivityCF({ activityId });

      if (!response.success) {
        if (existingActivity) {
          setActivities((prev) =>
            prev.map((activity) =>
              activity.id === activityId ? existingActivity : activity,
            ),
          );
        }
        addToast(response.message || "Failed to delete activity.", "error");
        return;
      }

      if (existingActivity) {
        runActivityBackgroundTask(
          (async () => {
            await queueUserAction(
              "delete",
              activityId,
              existingActivity.title,
              existingActivity.category,
              existingActivity.location,
            );

            analytics.logEvent("wii_activity_deleted", {
              id: activityId,
              title: existingActivity.title,
              createdBy: existingActivity.creator.id,
              category: existingActivity.category,
              groupSize: existingActivity.groupSize,
              duration: existingActivity.duration,
              deletedBy: currentUser.id,
              timestamp: new Date().toISOString(),
            });
            crashlytics.log(
              `Activity Deleted: ${existingActivity.title} (${activityId}) by ${currentUser.username} (${currentUser.id})`,
            );
          })(),
          `Error finishing delete activity for ${activityId}`,
        );
      }

      addToast("Activity deleted successfully!", "success");
    } catch (error) {
      if (existingActivity) {
        setActivities((prev) =>
          prev.map((activity) =>
            activity.id === activityId ? existingActivity : activity,
          ),
        );
      }
      crashlytics.logError(
        `Error deleting activity ${activityId}: ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
      addToast("Failed to delete activity. Please try again.", "error");
    }
  };

  const undeleteActivity = async (activityId: string) => {
    if (!currentUser) return;
    try {
      // Get activity metadata before restore for analytics
      const activity = activities.find((a) => a.id === activityId);

      // Call Cloud Function to undelete activity (handles all validation and DB operations)
      const response = await undeleteActivityCF({ activityId });

      if (!response.success) {
        addToast(response.message || "Failed to restore activity.", "error");
        return;
      }

      // Log activity restoration to Analytics
      if (activity) {
        analytics.logEvent("wii_activity_restored", {
          id: activityId,
          title: activity.title,
          createdBy: activity.creator.id,
          category: activity.category,
          groupSize: activity.groupSize,
          duration: activity.duration,
          restoredBy: currentUser.id,
          timestamp: new Date().toISOString(),
        });
        crashlytics.log(
          `Activity Restored: ${activity.title} (${activityId}) by ${currentUser.username} (${currentUser.id})`,
        );
      }

      addToast("Activity restored successfully!", "success");
    } catch (error) {
      crashlytics.logError(
        `Error restoring activity ${activityId}: ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
      addToast("Failed to restore activity. Please try again.", "error");
    }
  };

  const leaveActivity = async (activityId: string) => {
    if (!currentUser) return;

    const existingActivity = activities.find((a) => a.id === activityId);
    const optimisticActivity = existingActivity
      ? applyOptimisticLeave(existingActivity, currentUser.id)
      : null;

    if (optimisticActivity) {
      setActivities((prev) =>
        prev.map((activity) =>
          activity.id === activityId ? optimisticActivity : activity,
        ),
      );
    }

    try {
      // Call Cloud Function to leave activity (handles all validation and DB operations)
      const response = await leaveActivityCF({ activityId });

      if (!response.success) {
        if (existingActivity) {
          setActivities((prev) =>
            prev.map((activity) =>
              activity.id === activityId ? existingActivity : activity,
            ),
          );
        }
        addToast("Failed to leave activity.", "error");
        return;
      }

      if (existingActivity) {
        runActivityBackgroundTask(
          (async () => {
            await queueUserAction(
              "leave",
              activityId,
              existingActivity.title,
              existingActivity.category,
              existingActivity.location,
            );

            analytics.logEvent("wii_activity_left", {
              id: activityId,
              title: existingActivity.title,
              createdBy: existingActivity.creator.id,
              category: existingActivity.category,
              groupSize: existingActivity.groupSize,
              duration: existingActivity.duration,
              leftBy: currentUser.id,
              timestamp: new Date().toISOString(),
            });
            crashlytics.log(
              `Activity Left: ${existingActivity.title} (${activityId}) by ${currentUser.username} (${currentUser.id})`,
            );
          })(),
          `Error finishing leave activity for ${activityId}`,
        );
      }

      addToast("You have left the activity.", "success");
    } catch (error) {
      if (existingActivity) {
        setActivities((prev) =>
          prev.map((activity) =>
            activity.id === activityId ? existingActivity : activity,
          ),
        );
      }
      crashlytics.logError(
        `Error leaving activity ${activityId}: ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
      addToast("Failed to leave activity. Please try again.", "error");
    }
  };

  const kickParticipant = async (activityId: string, participantId: string) => {
    if (!currentUser) return;

    const existingActivity = activities.find((a) => a.id === activityId);
    const optimisticActivity = existingActivity
      ? applyOptimisticParticipantKick(existingActivity, participantId)
      : null;

    if (optimisticActivity) {
      setActivities((prev) =>
        prev.map((activity) =>
          activity.id === activityId ? optimisticActivity : activity,
        ),
      );
    }

    try {
      // Call Cloud Function to kick participant (handles all validation and DB operations)
      const response = await kickParticipantCF({ activityId, participantId });

      if (!response.success) {
        if (existingActivity) {
          setActivities((prev) =>
            prev.map((activity) =>
              activity.id === activityId ? existingActivity : activity,
            ),
          );
        }
        addToast("Failed to remove participant.", "error");
        return;
      }

      if (existingActivity) {
        runActivityBackgroundTask(
          (async () => {
            await queueUserAction(
              "kick",
              activityId,
              existingActivity.title,
              existingActivity.category,
              existingActivity.location,
            );

            const kickedUser = existingActivity.participants?.find(
              (participant: any) => participant.id === participantId,
            );
            analytics.logEvent("wii_activity_participant_kicked", {
              activityId,
              activityTitle: existingActivity.title,
              kickedUserId: participantId,
              kickedBy: currentUser.id,
              timestamp: new Date().toISOString(),
            });
            if (kickedUser) {
              crashlytics.log(
                `Participant Kicked: ${kickedUser.username} (${participantId}) from ${existingActivity.title} (${activityId}) by ${currentUser.username} (${currentUser.id})`,
              );
            }
          })(),
          `Error finishing kick participant for ${activityId}/${participantId}`,
        );
      }

      addToast("Participant removed successfully!", "success");
    } catch (error) {
      if (existingActivity) {
        setActivities((prev) =>
          prev.map((activity) =>
            activity.id === activityId ? existingActivity : activity,
          ),
        );
      }
      crashlytics.logError(
        `Error kicking participant ${participantId} from activity ${activityId}: ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
      addToast("Failed to remove participant. Please try again.", "error");
    }
  };

  const approveRejoinRequest = async (activityId: string, userId: string) => {
    if (!currentUser) return;

    const existingActivity = activities.find((a) => a.id === activityId);
    const optimisticActivity = existingActivity
      ? applyOptimisticApproveRejoin(existingActivity, userId)
      : null;

    if (optimisticActivity) {
      setActivities((prev) =>
        prev.map((activity) =>
          activity.id === activityId ? optimisticActivity : activity,
        ),
      );
    }

    try {
      // Call Cloud Function to approve rejoin request (handles all validation and DB operations)
      const response = await approveRejoinRequestCF({ activityId, userId });

      if (!response.success) {
        if (existingActivity) {
          setActivities((prev) =>
            prev.map((activity) =>
              activity.id === activityId ? existingActivity : activity,
            ),
          );
        }
        addToast("Failed to approve rejoin request.", "error");
        return;
      }

      runActivityBackgroundTask(
        Promise.resolve().then(() => {
          analytics.logEvent("wii_rejoin_request_approved", {
            activityId,
            organizerId: currentUser.id,
            requesterId: userId,
            timestamp: new Date().toISOString(),
          });
        }),
        `Error logging approved rejoin request for ${activityId}`,
      );

      addToast("Rejoin request approved!", "success");
    } catch (error) {
      if (existingActivity) {
        setActivities((prev) =>
          prev.map((activity) =>
            activity.id === activityId ? existingActivity : activity,
          ),
        );
      }
      crashlytics.logError(
        `Error approving rejoin request: ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
      addToast("Failed to approve rejoin request.", "error");
    }
  };

  const declineRejoinRequest = async (activityId: string, userId: string) => {
    if (!currentUser) return;

    const existingActivity = activities.find((a) => a.id === activityId);
    const optimisticActivity = existingActivity
      ? applyOptimisticDeclineRejoin(existingActivity, userId)
      : null;

    if (optimisticActivity) {
      setActivities((prev) =>
        prev.map((activity) =>
          activity.id === activityId ? optimisticActivity : activity,
        ),
      );
    }

    try {
      // Call Cloud Function to decline rejoin request (handles all validation and DB operations)
      const response = await declineRejoinRequestCF({ activityId, userId });

      if (!response.success) {
        if (existingActivity) {
          setActivities((prev) =>
            prev.map((activity) =>
              activity.id === activityId ? existingActivity : activity,
            ),
          );
        }
        addToast("Failed to decline rejoin request.", "error");
        return;
      }

      runActivityBackgroundTask(
        Promise.resolve().then(() => {
          analytics.logEvent("wii_rejoin_request_declined", {
            activityId,
            organizerId: currentUser.id,
            requesterId: userId,
            timestamp: new Date().toISOString(),
          });
        }),
        `Error logging declined rejoin request for ${activityId}`,
      );

      addToast("Rejoin request declined.", "info");
    } catch (error) {
      if (existingActivity) {
        setActivities((prev) =>
          prev.map((activity) =>
            activity.id === activityId ? existingActivity : activity,
          ),
        );
      }
      crashlytics.logError(
        `Error declining rejoin request: ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
      addToast("Failed to decline rejoin request.", "error");
    }
  };

  const inviteUsersToActivity = async (
    activityId: string,
    userIds: string[],
  ) => {
    if (!currentUser || userIds.length === 0) return;

    const existingActivity = activities.find((a) => a.id === activityId);
    const optimisticActivity = existingActivity
      ? applyOptimisticInviteUsers(existingActivity, userIds)
      : null;

    if (optimisticActivity) {
      setActivities((prev) =>
        prev.map((activity) =>
          activity.id === activityId ? optimisticActivity : activity,
        ),
      );
    }

    try {
      // Call Cloud Function to invite users (handles all validation and DB operations)
      const response = await inviteUsersToActivityCF({ activityId, userIds });

      if (!response.success) {
        if (existingActivity) {
          setActivities((prev) =>
            prev.map((activity) =>
              activity.id === activityId ? existingActivity : activity,
            ),
          );
        }
        addToast("Failed to send invitations.", "error");
        return;
      }

      runActivityBackgroundTask(
        Promise.resolve().then(() => {
          analytics.logEvent("wii_activity_invites_sent", {
            activityId,
            inviterId: currentUser.id,
            invitedCount: userIds.length,
            timestamp: new Date().toISOString(),
          });
        }),
        `Error logging invitations for ${activityId}`,
      );

      addToast(
        `Invited ${userIds.length} user${userIds.length > 1 ? "s" : ""}!`,
        "success",
      );
    } catch (error) {
      if (existingActivity) {
        setActivities((prev) =>
          prev.map((activity) =>
            activity.id === activityId ? existingActivity : activity,
          ),
        );
      }
      crashlytics.logError(
        `Error inviting users to activity: ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
      addToast("Failed to send invitations.", "error");
    }
  };

  const respondToInvite = async (activityId: string, accept: boolean) => {
    if (!currentUser) return;

    const existingActivity = activities.find((a) => a.id === activityId);
    const optimisticActivity = existingActivity
      ? applyOptimisticInviteResponse(existingActivity, currentUser, accept)
      : null;

    if (optimisticActivity) {
      setActivities((prev) =>
        prev.map((activity) =>
          activity.id === activityId ? optimisticActivity : activity,
        ),
      );
    }

    try {
      // Call Cloud Function to respond to invite (handles all validation and DB operations)
      const response = await respondToInviteCF({ activityId, accept });

      if (!response.success) {
        if (existingActivity) {
          setActivities((prev) =>
            prev.map((activity) =>
              activity.id === activityId ? existingActivity : activity,
            ),
          );
        }
        addToast("Failed to respond to invitation.", "error");
        return;
      }

      runActivityBackgroundTask(
        (async () => {
          await markInviteNotificationsAsRead(activityId);

          if (accept) {
            analytics.logEvent("wii_activity_invite_accepted", {
              activityId,
              userId: currentUser.id,
              creatorId: existingActivity?.creator.id,
              timestamp: new Date().toISOString(),
            });
          } else {
            analytics.logEvent("wii_activity_invite_declined", {
              activityId,
              userId: currentUser.id,
              creatorId: existingActivity?.creator.id,
              timestamp: new Date().toISOString(),
            });
          }
        })(),
        `Error finishing invite response for ${activityId}`,
      );

      if (accept) {
        addToast("Invitation accepted! You joined the activity.", "success");
      } else {
        addToast("Invitation declined.", "info");
      }
    } catch (error) {
      if (existingActivity) {
        setActivities((prev) =>
          prev.map((activity) =>
            activity.id === activityId ? existingActivity : activity,
          ),
        );
      }
      crashlytics.logError(
        `Error responding to invite: ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
      addToast("Failed to respond to invitation.", "error");
    }
  };

  const cancelInvite = async (activityId: string, userId: string) => {
    if (!currentUser) return;

    const existingActivity = activities.find((a) => a.id === activityId);
    const optimisticActivity = existingActivity
      ? applyOptimisticCancelInvite(existingActivity, userId)
      : null;

    if (optimisticActivity) {
      setActivities((prev) =>
        prev.map((activity) =>
          activity.id === activityId ? optimisticActivity : activity,
        ),
      );
    }

    try {
      // Call Cloud Function to cancel invite (handles all validation and DB operations)
      const response = await cancelInviteCF({ activityId, userId });

      if (!response.success) {
        if (existingActivity) {
          setActivities((prev) =>
            prev.map((activity) =>
              activity.id === activityId ? existingActivity : activity,
            ),
          );
        }
        addToast("Failed to cancel invitation.", "error");
        return;
      }

      runActivityBackgroundTask(
        Promise.resolve().then(() => {
          analytics.logEvent("wii_activity_invite_canceled", {
            activityId,
            creatorId: currentUser.id,
            canceledUserId: userId,
            timestamp: new Date().toISOString(),
          });
        }),
        `Error logging invite cancellation for ${activityId}`,
      );

      addToast("Invitation canceled.", "success");
    } catch (error) {
      if (existingActivity) {
        setActivities((prev) =>
          prev.map((activity) =>
            activity.id === activityId ? existingActivity : activity,
          ),
        );
      }
      crashlytics.logError(
        `Error canceling invite: ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
      addToast("Failed to cancel invitation.", "error");
    }
  };

  const markBatchAsRead = async (notificationsToUpdate: Notification[]) => {
    if (!db || notificationsToUpdate.length === 0) return;
    const batch = writeBatch(db);
    notificationsToUpdate.forEach((n) => {
      if (!db || !n.id) return;
      const notifRef = doc(db, "notifications", n.id);
      batch.update(notifRef, { isRead: true });
    });
    await batch.commit();
  };

  const markChatNotificationsAsRead = (chatId: string) => {
    const toUpdate = notifications.filter(
      (n) => n.chatId === chatId && !n.isRead,
    );
    markBatchAsRead(toUpdate);
  };

  const markJoinNotificationsAsRead = () => {
    const toUpdate = notifications.filter(
      (n) => n.type === "JOIN" && !n.isRead,
    );
    markBatchAsRead(toUpdate);
  };

  const markNotificationsAsReadByType = async (
    type: Notification["type"],
    fromUserId: string,
  ) => {
    if (!currentUser) return;

    try {
      // Call Cloud Function to mark notifications by type and sender
      const response = await markNotificationsAsRead({
        type,
        fromUserId,
      });

      if (!response.success) {
        throw new Error("Failed to mark notifications as read");
      }

      // Log analytics event
      analytics.logEvent("wii_notifications_marked_read", {
        type,
        fromUserId,
        count: response.markedCount,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      crashlytics.logError(
        `Error marking notifications as read by type ${type}: ${JSON.stringify(error)}`,
        "ActivityContext.tsx",
        error,
      );
    }
  };

  const markNotificationAsRead = (notificationId: string) => {
    const notification = notifications.find(
      (n) => n.id === notificationId && !n.isRead,
    );
    if (notification) {
      markBatchAsRead([notification]);
    }
  };

  const markAllNotificationsAsRead = async () => {
    // Exclude notifications that require action (pending follow requests, rejoin requests, and activity invites)
    const toUpdate = notifications.filter(
      (n) =>
        !n.isRead &&
        !(n.type === "FOLLOW_REQUEST" && !n.isRead) &&
        !(n.type === "REJOIN_REQUEST" && !n.isRead) &&
        !(n.type === "ACTIVITY_INVITE" && !n.isRead),
    );
    await markBatchAsRead(toUpdate);
  };

  const actionsContextValue: ActivityActionsContextType = useMemo(
    () => ({
      loadAuditLogs,
      createActivity,
      updateActivity,
      deleteActivity,
      undeleteActivity,
      joinActivity,
      leaveActivity,
      kickParticipant,
      approveRejoinRequest,
      declineRejoinRequest,
      passActivity,
      inviteUsersToActivity,
      respondToInvite,
      cancelInvite,
      createOrGetDirectChat,
      sendMessage,
      toggleStarMessage,
      forwardMessages,
      addReactionToMessage,
      editMessage,
      deleteMessage,
      markMessageAsDelivered,
      markMessageAsRead,
      markChatNotificationsAsRead,
      markJoinNotificationsAsRead,
      markNotificationsAsReadByType,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      createUserJoinedAuditLog,
      fetchRecommendations,
      prefetchIdeas,
      loadMoreActivities,
      refreshActivities,
      refreshSingleActivity,
      queueUserAction,
    }),
    [
      loadAuditLogs,
      createActivity,
      updateActivity,
      deleteActivity,
      undeleteActivity,
      joinActivity,
      leaveActivity,
      kickParticipant,
      approveRejoinRequest,
      declineRejoinRequest,
      passActivity,
      inviteUsersToActivity,
      respondToInvite,
      cancelInvite,
      createOrGetDirectChat,
      sendMessage,
      toggleStarMessage,
      forwardMessages,
      addReactionToMessage,
      editMessage,
      deleteMessage,
      markMessageAsDelivered,
      markMessageAsRead,
      markChatNotificationsAsRead,
      markJoinNotificationsAsRead,
      markNotificationsAsReadByType,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      createUserJoinedAuditLog,
      fetchRecommendations,
      prefetchIdeas,
      loadMoreActivities,
      refreshActivities,
      refreshSingleActivity,
      queueUserAction,
    ],
  );

  const feedDataContextValue: ActivityFeedDataContextType = useMemo(
    () => ({
      activities,
      isLoading,
      isLoadingMore,
      hasMoreActivities,
      refreshing,
      passedActivityIds,
    }),
    [
      activities,
      isLoading,
      isLoadingMore,
      hasMoreActivities,
      refreshing,
      passedActivityIds,
    ],
  );

  const chatDataContextValue: ActivityChatDataContextType = useMemo(
    () => ({
      chats,
      getUnreadMessageCount,
      getUnreadGroupChatCount,
      getUnreadDirectChatCount,
      getChatsForUser,
      getChatById,
      getDirectChatId,
      getUnreadMessageCountForChat,
    }),
    [
      chats,
      getUnreadMessageCount,
      getUnreadGroupChatCount,
      getUnreadDirectChatCount,
      getChatsForUser,
      getChatById,
      getDirectChatId,
      getUnreadMessageCountForChat,
    ],
  );

  const notificationsDataContextValue: ActivityNotificationsDataContextType = useMemo(
    () => ({
      notifications,
      getUnreadNotificationCount,
    }),
    [notifications, getUnreadNotificationCount],
  );

  const recommendationsDataContextValue: ActivityRecommendationsDataContextType = useMemo(
    () => ({
      recommendations,
      vibeCheckUsers,
      isLoadingRecommendations,
      prefetchedIdeas,
    }),
    [
      recommendations,
      vibeCheckUsers,
      isLoadingRecommendations,
      prefetchedIdeas,
    ],
  );

  const auditDataContextValue: ActivityAuditDataContextType = useMemo(
    () => ({
      auditLogs,
      isLoadingAuditLogs,
      getAuditLogsForUser,
      getAuditLogsForActivity,
    }),
    [
      auditLogs,
      isLoadingAuditLogs,
      getAuditLogsForUser,
      getAuditLogsForActivity,
    ],
  );

  return (
    <ActivityActionsContext.Provider value={actionsContextValue}>
      <ActivityFeedDataContext.Provider value={feedDataContextValue}>
        <ActivityChatDataContext.Provider value={chatDataContextValue}>
          <ActivityNotificationsDataContext.Provider value={notificationsDataContextValue}>
            <ActivityRecommendationsDataContext.Provider value={recommendationsDataContextValue}>
              <ActivityAuditDataContext.Provider value={auditDataContextValue}>
                {children}
              </ActivityAuditDataContext.Provider>
            </ActivityRecommendationsDataContext.Provider>
          </ActivityNotificationsDataContext.Provider>
        </ActivityChatDataContext.Provider>
      </ActivityFeedDataContext.Provider>
    </ActivityActionsContext.Provider>
  );
};

export const useActivityActions = (): ActivityActionsContextType => {
  const context = useContext(ActivityActionsContext);

  if (!context) {
    throw new Error("useActivityActions must be used within an ActivityProvider");
  }

  return context;
};

export const useActivityFeedData = (): ActivityFeedDataContextType => {
  const context = useContext(ActivityFeedDataContext);

  if (!context) {
    throw new Error("useActivityFeedData must be used within an ActivityProvider");
  }

  return context;
};

export const useActivityChatData = (): ActivityChatDataContextType => {
  const context = useContext(ActivityChatDataContext);

  if (!context) {
    throw new Error("useActivityChatData must be used within an ActivityProvider");
  }

  return context;
};

export const useActivityNotificationsData = (): ActivityNotificationsDataContextType => {
  const context = useContext(ActivityNotificationsDataContext);

  if (!context) {
    throw new Error(
      "useActivityNotificationsData must be used within an ActivityProvider",
    );
  }

  return context;
};

export const useActivityRecommendationsData = (): ActivityRecommendationsDataContextType => {
  const context = useContext(ActivityRecommendationsDataContext);

  if (!context) {
    throw new Error(
      "useActivityRecommendationsData must be used within an ActivityProvider",
    );
  }

  return context;
};

export const useActivityAuditData = (): ActivityAuditDataContextType => {
  const context = useContext(ActivityAuditDataContext);

  if (!context) {
    throw new Error("useActivityAuditData must be used within an ActivityProvider");
  }

  return context;
};
